import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE_URL } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContextNew";
import { useTheme } from "../context/ThemeContext";
import {
  decryptTextFromMessage,
  encryptOutgoingMessage,
  getOrCreateRsaKeyPair,
  reencryptAesKeyForRecipients,
  encryptDatabaseBackup,
  decryptDatabaseBackup,
  hashBackupPin
} from "../utils/e2ee";
import {
  saveLocalChat,
  getLocalChats,
  saveLocalMessage,
  getLocalMessages,
  deleteLocalMessage,
  clearLocalDb,
  exportLocalDbAsJson,
  importLocalDbFromJson,
  deleteLocalChat,
  clearLocalMessagesForChat
} from "../utils/localDb";
import SidebarPanel from "../components/sidebar/SidebarPanel";
import ChatPane from "../components/chat/ChatPane";
import CallsPanel from "../components/panels/CallsPanel";
import StatusPanel from "../components/panels/StatusPanel";
import ProfileDrawer from "../components/common/ProfileDrawer";
import SettingsDrawer from "../components/common/SettingsDrawer";
import ToastStack from "../components/common/ToastStack";
import CallOverlay from "../components/chat/CallOverlay";
import CallLogsDrawer from "../components/chat/CallLogsDrawer";
import CreateGroupModal from "../components/common/CreateGroupModal";
import HideMessageModal from "../components/common/HideMessageModal";
import UnlockMessageModal from "../components/common/UnlockMessageModal";
import HiddenVaultDrawer from "../components/common/HiddenVaultDrawer";
import ChangeKeyModal from "../components/common/ChangeKeyModal";
import BackupSetupModal from "../components/common/BackupSetupModal";
import RestoreBackupModal from "../components/common/RestoreBackupModal";
import { appendCallLog, patchCallLog, getCallLogs } from "../utils/callLogs";
import { navigateTo } from "../utils/nav";
import useCallStats from "../hooks/useCallStats";
import CallSummary from "../components/chat/CallSummary";
import { ENABLE_CALL_SUMMARY } from "../utils/featureFlags";


const IDLE_CALL = {
  phase: "idle",
  callType: "voice",
  peerUserId: null,
  peerName: "",
  peerAvatar: null,
  chatId: null,
  micEnabled: true,
  videoEnabled: true,
  screenSharing: false,
  mode: "standard",
  startedAt: null,
  logId: null
};

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
    { urls: "stun:stun4.l.google.com:19302" }
  ]
};
const DEFAULT_UI_SETTINGS = {
  compactMode: true,
  showOnlineStatus: true,
  enterToSend: true,
  soundEffects: true,
  notificationsEnabled: true,
  callEffectsEnabled: true,
  autoReactionsEnabled: true
};
const CHAT_PIN_PATTERN = /^\d{4,8}$/;
const RECIPIENT_CACHE_TTL_MS = 2 * 60 * 1000;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
}

function stopStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

function mediaAccessErrorMessage(error, callType = "voice") {
  const needs = callType === "video" ? "camera and microphone" : "microphone";
  const name = (error?.name || "").toString();
  const raw = (error?.message || "").toString().trim();
  const message = raw.toLowerCase();

  if (!navigator?.mediaDevices?.getUserMedia) {
    return "This browser does not support voice/video calls.";
  }
  if (!window.isSecureContext) {
    return "Calls require HTTPS (or localhost). Open the app on a secure origin and retry.";
  }
  if (
    name === "NotAllowedError"
    || name === "PermissionDeniedError"
    || message.includes("permission denied")
    || message.includes("denied by system")
  ) {
    return `Permission denied. Allow ${needs} in browser site settings and Windows Privacy settings, then retry.`;
  }
  if (name === "NotFoundError" || message.includes("requested device not found")) {
    return `No ${callType === "video" ? "camera/microphone" : "microphone"} device found.`;
  }
  if (name === "NotReadableError" || message.includes("device in use")) {
    return `${callType === "video" ? "Camera/microphone" : "Microphone"} is busy in another app. Close other app and retry.`;
  }
  return raw || "Unable to access media device.";
}

function sortChats(list) {
  return [...list].sort((a, b) => {
    const aPriority = a?.chat_type === "self" ? 0 : 1;
    const bPriority = b?.chat_type === "self" ? 0 : 1;
    if (aPriority !== bPriority) return aPriority - bPriority;
    const left = new Date(b.last_message_at || b.last_message_created_at || 0).getTime();
    const right = new Date(a.last_message_at || a.last_message_created_at || 0).getTime();
    return left - right;
  });
}

function createClientMessageId() {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const PINNED_CHATS_STORAGE_KEY = "ana_pinned_chats_v1";

export default function ChatPage() {
  const { user, logout, reload } = useAuth();
  const socket = useSocket();
  const {
    theme,
    toggleTheme,
    accentColor,
    setAccentColor,
    doodleStyle,
    setDoodleStyle,
    sidebarColor,
    chatPaneColor,
    setSidebarColor,
    setChatPaneColor,
    isSidebarLight,
    isChatPaneLight
  } = useTheme();
  const isMobile = useIsMobile();

  const sidebarTabStorageKey = useMemo(() => `anach_sidebar_tab_v1_${user?.id || "guest"}`, [user?.id]);
  const [activeSidebarTab, setActiveSidebarTab] = useState(() => {
    try {
      const stored = (window.localStorage.getItem(`anach_sidebar_tab_v1_${user?.id || "guest"}`) || "").toString();
      if (stored === "status" || stored === "calls" || stored === "chats") return stored;
    } catch {
      // ignore
    }
    return "chats";
  });

  const [chats, setChats] = useState([]);
  const [hiddenChats, setHiddenChats] = useState([]);
  const [hiddenChatsCount, setHiddenChatsCount] = useState(0);
  const [pinnedChatIds, setPinnedChatIds] = useState(() => {
    try {
      const raw = localStorage.getItem(`ana_pinned_chats_v1_${user?.id || "guest"}`);
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) ? parsed.map((id) => Number(id)).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [chatPinCache, setChatPinCache] = useState("");
  const [loadingChats, setLoadingChats] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [selectedStatusFeed, setSelectedStatusFeed] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typingName, setTypingName] = useState("");
  // Per-Message Hidden Message System States
  const [unlockedMessages, setUnlockedMessages] = useState(new Map());
  const [hiddenVaultOpen, setHiddenVaultOpen] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [changeKeyModalOpen, setChangeKeyModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unlockError, setUnlockError] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [hideBusy, setHideBusy] = useState(false);
  const [autoLockSetting, setAutoLockSetting] = useState(() => {
    return localStorage.getItem(`anachat_auto_lock_setting_${user?.id || "guest"}`) || "app_closed";
  });
  const [search, setSearch] = useState("");
  const [peopleResults, setPeopleResults] = useState([]);
  const [searchingPeople, setSearchingPeople] = useState(false);
  const [unreadByChat, setUnreadByChat] = useState({});
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [callLogsOpen, setCallLogsOpen] = useState(false);
  const [backupStatus, setBackupStatus] = useState("Disabled");
  const [backupSize, setBackupSize] = useState("N/A");
  const [lastBackup, setLastBackup] = useState("Never");
  const [backupSetupModalOpen, setBackupSetupModalOpen] = useState(false);
  const [restoreBackupModalOpen, setRestoreBackupModalOpen] = useState(false);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [lastBackupInfo, setLastBackupInfo] = useState(null);
  const [backupSetupBusy, setBackupSetupBusy] = useState(false);
  const [restoreBackupBusy, setRestoreBackupBusy] = useState(false);
  const sessionBackupKeyRef = useRef(null);


  const [settingsSaving, setSettingsSaving] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [groupUsersLoading, setGroupUsersLoading] = useState(false);
  const [groupCreating, setGroupCreating] = useState(false);
  const [blockActionBusy, setBlockActionBusy] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState({});
  const [modalConfig, setModalConfig] = useState(null);
  const [userSettings, setUserSettings] = useState({
    ...DEFAULT_UI_SETTINGS
  });
  const [toasts, setToasts] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      const alt = e.altKey;
      const key = (e.key || "").toLowerCase();

      // 1. Disable browser defaults for Ctrl+P, Ctrl+S, Ctrl+F, Ctrl+D on main page
      if (ctrl && !alt) {
        if (key === "p" || key === "s" || key === "f" || key === "d") {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // 2. Escape key handling
      if (e.key === "Escape") {
        setReplyToMessage(null);
        setSettingsOpen(false);
        setProfileOpen(false);
        setGroupModalOpen(false);
        setCallLogsOpen(false);
      }

      // 3. Global hotkeys (Ctrl + Alt + [Letter])
      if (ctrl && alt) {
        if (key === "s") {
          e.preventDefault();
          setSettingsOpen((prev) => !prev);
        } else if (key === "p") {
          e.preventDefault();
          setProfileOpen((prev) => !prev);
        } else if (key === "n") {
          e.preventDefault();
          setGroupModalOpen((prev) => !prev);
        } else if (key === "a") {
          e.preventDefault();
          toggleTheme?.();
        }
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, [toggleTheme]);

  const [call, setCall] = useState(IDLE_CALL);
  const [localCallStream, setLocalCallStream] = useState(null);
  const [remoteCallStream, setRemoteCallStream] = useState(null);
  const [peerConn, setPeerConn] = useState(null); // reactive shadow of peerRef.current
  const [watchSessions, setWatchSessions] = useState({});
  const [callChatMessages, setCallChatMessages] = useState([]);

  const [callMoments, setCallMoments] = useState([]);
  const [callSummary, setCallSummary] = useState(null);
  const [callSummaryOpen, setCallSummaryOpen] = useState(false);

  const notify = useCallback((toast) => {
    const id = `${Date.now()}-${Math.random()}`;
    const normalized = { id, type: "info", ...toast };
    setToasts((prev) => [...prev, normalized]);

    const notificationOn = settingsRef.current.notificationsEnabled !== false;
    const soundOn = settingsRef.current.soundEffects !== false;
    if (notificationOn && soundOn) {
      playMessageTone(normalized.type);
    }

    // Show browser notification for priority notifications (calls, messages) even if tab is visible
    // This ensures user doesn't miss important notifications
    const isPriorityNotification = toast?.isPriority === true;
    if (
      notificationOn
      && typeof Notification !== "undefined"
      && Notification.permission === "granted"
      && typeof document !== "undefined"
      && (isPriorityNotification || document.visibilityState !== "visible")
    ) {
      const title = normalized.title || "Secure Chat";
      const body = normalized.message || "";
      if (body) {
        try {
          if (typeof window !== "undefined" && typeof window.Notification === "function") {
            const notification = new window.Notification(title, {
              body,
              badge: "/logo.png",
              tag: isPriorityNotification ? "priority-notification" : undefined
            });
            setTimeout(() => {
              try { notification.close(); } catch {}
            }, isPriorityNotification ? 10000 : 5000);
          } else {
            throw new Error("Notification constructor not supported");
          }
        } catch (err) {
          // On mobile browsers, direct Notification construction throws. Fall back to Service Worker if available.
          if (navigator.serviceWorker && navigator.serviceWorker.ready) {
            navigator.serviceWorker.ready.then((registration) => {
              registration.showNotification(title, {
                body,
                badge: "/logo.png",
                tag: isPriorityNotification ? "priority-notification" : undefined
              });
            }).catch((swErr) => {
              console.warn("[Notification] SW fallback failed:", swErr);
            });
          } else {
            console.warn("[Notification] Constructor not supported and no SW ready.");
          }
        }
      }
    }

    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((entry) => entry.id !== id));
      toastTimers.current.delete(id);
    }, 3600);
    toastTimers.current.set(id, timer);
  }, []);

  // Relationship data & Pending requests
  const [relationshipData, setRelationshipData] = useState(null);

  const fetchRelationshipStatus = useCallback(() => {
    if (!user?.id) return;
    api.get("/users/relationship/status")
      .then(({ data }) => setRelationshipData(data))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    fetchRelationshipStatus();
  }, [fetchRelationshipStatus]);

  // Listen to socket for real-time relationship request notifications
  useEffect(() => {
    if (!socket) return;
    const handleReqReceived = (data) => {
      fetchRelationshipStatus();
      notify({
        type: "info",
        title: "Relationship Request 💖",
        message: `${data.requesterName || "Someone"} sent you a relationship request!`
      });
    };
    const handleReqAccepted = (data) => {
      fetchRelationshipStatus();
      notify({
        type: "success",
        title: "Request Accepted! 💑",
        message: `${data.partnerName || "Your partner"} accepted your relationship request!`
      });
    };

    socket.on("relationship_request_received", handleReqReceived);
    socket.on("relationship_request_accepted", handleReqAccepted);

    return () => {
      socket.off("relationship_request_received", handleReqReceived);
      socket.off("relationship_request_accepted", handleReqAccepted);
    };
  }, [fetchRelationshipStatus, notify, socket]);

  const activeChatIdRef = useRef(null);
  const callRef = useRef(IDLE_CALL);
  const callLogIdRef = useRef(null);
  const callSnapshotRef = useRef(null); // captures call data before reset

  // Poll real WebRTC stats during active call and patch call log
  const callStats = useCallStats(peerConn, call.phase === "active");

  // Patch call log whenever stats update
  useEffect(() => {
    if (!callStats || call.phase !== "active" || !user?.id) return;
    const logId = callRef.current?.logId || callLogIdRef.current;
    if (!logId) return;
    patchCallLog(user.id, logId, {
      bytesSent:         callStats.bytesSent,
      bytesReceived:     callStats.bytesReceived,
      networkType:       callStats.networkType,
      callQuality:       callStats.callQuality,
      reconnectCount:    callStats.reconnectCount,
      interruptionCount: callStats.interruptionCount,
    });
  }, [callStats, call.phase, user?.id]);



  const chatsRef = useRef([]);
  const peerRef = useRef(null);
  const incomingOfferRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const audioSenderRef = useRef(null);
  const videoSenderRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const screenStreamRef = useRef(null);
  const deliveredMessageIdsRef = useRef(new Set());
  const typingTimer = useRef(null);
  const toastTimers = useRef(new Map());
  const settingsRef = useRef(userSettings);
  const chatRecipientsCacheRef = useRef(new Map());
  const chatUpdatedTimerRef = useRef(null);
  const lastChatMessageEventAtRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = toastTimers.current.get(id);
    if (timer) clearTimeout(timer);
    toastTimers.current.delete(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const decryptMessageForMe = useCallback(async (message) => {
    if (!message || !user?.id) return message;
    const pair = await getOrCreateRsaKeyPair(user.id);

    let body = typeof message.body === "string" ? message.body : null;
    const key = message.e2ee?.key || message.e2ee?.keys?.[String(user.id)] || null;
    if ((!body || body.length === 0) && key && message.e2ee?.text) {
      try {
        body = await decryptTextFromMessage({
          e2ee: { ...message.e2ee, key },
          privateJwk: pair.privateJwk
        });
      } catch {
        body = null;
      }
    }

    let replyBody = typeof message.reply_to_body === "string" ? message.reply_to_body : null;
    const replyKey = message.reply_to_e2ee?.key || message.reply_to_e2ee?.keys?.[String(user.id)] || null;
    if ((!replyBody || replyBody.length === 0) && replyKey && message.reply_to_e2ee?.text) {
      try {
        replyBody = await decryptTextFromMessage({
          e2ee: { ...message.reply_to_e2ee, key: replyKey },
          privateJwk: pair.privateJwk
        });
      } catch {
        replyBody = null;
      }
    }

    return {
      ...message,
      body: body != null ? body : null,
      reply_to_body: replyBody != null ? replyBody : null
    };
  }, [user?.id]);

  const getChatRecipients = useCallback(async (chatId) => {
    const normalizedChatId = Number(chatId);
    if (!normalizedChatId || !user?.id) return [];

    const now = Date.now();
    const cached = chatRecipientsCacheRef.current.get(normalizedChatId);
    let list = cached?.participants || [];

    if (!cached || !cached.fetchedAt || now - cached.fetchedAt > RECIPIENT_CACHE_TTL_MS) {
      const { data } = await api.get(`/chats/${normalizedChatId}/participants`);
      list = Array.isArray(data?.participants) ? data.participants : [];
      chatRecipientsCacheRef.current.set(normalizedChatId, { fetchedAt: now, participants: list });
    }

    const pair = await getOrCreateRsaKeyPair(user.id);

    const recipients = list.map((entry) => ({
      id: entry.id,
      publicKey: entry.id === user.id ? pair.publicJwk : entry.publicKey
    }));

    const missing = recipients.find((entry) => !entry.publicKey);
    if (missing) {
      const err = new Error("Recipient has not set up encryption keys yet. Ask them to login once and retry.");
      err.code = "E2EE_KEY_MISSING";
      throw err;
    }

    return recipients;
  }, [user?.id]);

  const decryptChatPreviewForMe = useCallback(async (chat) => {
    return chat;
  }, []);

  const loadMessages = useCallback(async (chatId) => {
    if (!chatId || !user?.id) return;
    const timer = setTimeout(() => {
      setLoadingMessages(true);
    }, 150);
    try {
      const localMsgs = await getLocalMessages(chatId, user.id);
      let serverMsgs = [];
      try {
        const { data } = await api.get(`/messages/${chatId}`);
        const rawServerMsgs = Array.isArray(data) ? data : [];
        serverMsgs = await Promise.all(rawServerMsgs.map((item) => decryptMessageForMe(item)));
        await Promise.all(serverMsgs.map((item) => saveLocalMessage(item, user.id).catch(() => {})));
      } catch (err) {
        console.warn("Failed to sync server messages:", err);
      }
      const messageMap = new Map();
      [...localMsgs, ...serverMsgs].forEach((item) => {
        if (item?.id) messageMap.set(String(item.id), item);
      });
      const mergedMsgs = [...messageMap.values()].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      clearTimeout(timer);
      setMessages(mergedMsgs);
      setUnreadByChat((prev) => ({ ...prev, [chatId]: 0 }));
      setLoadingMessages(false);
      return true;
    } catch (err) {
      clearTimeout(timer);
      setMessages([]);
      setLoadingMessages(false);
      notify({ type: "error", message: "Unable to load messages locally." });
      return false;
    }
  }, [decryptMessageForMe, notify, user?.id]);

  useEffect(() => {
    const merged = {
      ...DEFAULT_UI_SETTINGS,
      ...(user?.settings || {}),
      compactMode: true
    };
    setUserSettings(merged);
  }, [user?.settings]);

  useEffect(() => {
    if (!user?.id) return;
    if (user?.anaSecurityPinEnabled) return;
    try {
      const key = `anach_ana_security_prompt_dismissed_v1_${user.id}`;
      const dismissed = window.localStorage.getItem(key) === "1";
      if (!dismissed) {
        setSettingsOpen(true);
        window.localStorage.setItem(key, "1");
        notify({ type: "info", title: "Ana Security", message: "Set a device PIN to protect new-device logins." });
      }
    } catch {
      // ignore storage failures
    }
  }, [notify, user?.anaSecurityPinEnabled, user?.id]);

  // Handle auto-lock visibility changes (e.g. app in background / closed)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && autoLockSetting === "app_closed") {
        setUnlockedMessages(new Map());
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [autoLockSetting]);

  // Handle auto-lock on active chat change (leaving chat)
  useEffect(() => {
    if (autoLockSetting === "leaving_chat") {
      setUnlockedMessages(new Map());
    }
  }, [activeChat?.id, autoLockSetting]);

  // Hide Message confirmation handler
  const handleHideConfirm = async ({ keyType, key }) => {
    if (!selectedMessage) return;
    setHideBusy(true);
    try {
      await api.post(`/messages/${selectedMessage.id}/hide`, {
        chatId: selectedMessage.chat_id,
        keyType,
        key
      });
      // Clear from unlocked locally
      setUnlockedMessages((prev) => {
        const next = new Map(prev);
        next.delete(Number(selectedMessage.id));
        return next;
      });
      notify({ type: "success", message: "Message hidden successfully!" });
      setHideModalOpen(false);
      // Reload message list to update masked text
      await loadMessages(selectedMessage.chat_id);
    } catch (err) {
      notify({ type: "error", message: err.response?.data?.message || "Failed to hide message." });
    } finally {
      setHideBusy(false);
    }
  };

  // Unlock Message confirmation handler
  const handleUnlockConfirm = async (key) => {
    if (!selectedMessage) return;
    setUnlockBusy(true);
    setUnlockError("");
    try {
      const { data } = await api.post(`/messages/${selectedMessage.id}/verify-key`, {
        key,
        chatId: selectedMessage.chat_id
      });
      
      const unmasked = data.message;
      setUnlockedMessages((prev) => {
        const next = new Map(prev);
        next.set(Number(unmasked.id), {
          ...unmasked,
          is_unlocked_message: true // flag to show Change Key / Hide Again options
        });
        return next;
      });

      // Handle custom auto-lock timers
      if (autoLockSetting === "5_min" || autoLockSetting === "15_min") {
        const durationMs = (autoLockSetting === "5_min" ? 5 : 15) * 60 * 1000;
        setTimeout(() => {
          setUnlockedMessages((prev) => {
            const next = new Map(prev);
            next.delete(Number(unmasked.id));
            return next;
          });
        }, durationMs);
      }

      notify({ type: "success", message: "🔒 Message unlocked!" });
      setUnlockModalOpen(false);
    } catch (err) {
      setUnlockError(err.response?.data?.message || "Incorrect unlock key");
    } finally {
      setUnlockBusy(false);
    }
  };

  const [changeKeyBusy, setChangeKeyBusy] = useState(false);
  const [changeKeyError, setChangeKeyError] = useState("");

  const handleChangeKeyConfirm = async ({ oldKey, newKey, newKeyType }) => {
    if (!selectedMessage) return;
    setChangeKeyBusy(true);
    setChangeKeyError("");
    try {
      await api.post(`/messages/${selectedMessage.id}/change-key`, {
        oldKey,
        newKey,
        newKeyType,
        chatId: selectedMessage.chat_id
      });
      notify({ type: "success", message: "Secret key changed successfully!" });
      setChangeKeyModalOpen(false);
    } catch (err) {
      setChangeKeyError(err.response?.data?.message || "Failed to change key");
    } finally {
      setChangeKeyBusy(false);
    }
  };

  // Custom events registry for hidden message actions
  useEffect(() => {
    const onHideMsgEvent = (e) => {
      setSelectedMessage(e.detail);
      setHideModalOpen(true);
    };
    const onUnlockMsgEvent = (e) => {
      setSelectedMessage(e.detail);
      setUnlockError("");
      setUnlockModalOpen(true);
    };
    const onUnhideMsgEvent = async (e) => {
      const msg = e.detail;
      if (window.confirm("Remove protection for this message? It will be visible to everyone normally.")) {
        try {
          await api.post(`/messages/${msg.id}/unhide`, { chatId: msg.chat_id });
          setUnlockedMessages((prev) => {
            const next = new Map(prev);
            next.delete(Number(msg.id));
            return next;
          });
          await loadMessages(msg.chat_id);
          notify({ type: "success", message: "Protection removed." });
        } catch (err) {
          notify({ type: "error", message: "Failed to remove protection." });
        }
      }
    };
    const onChangeKeyEvent = (e) => {
      setSelectedMessage(e.detail);
      setChangeKeyModalOpen(true);
    };
    const onRehideMsgEvent = (e) => {
      const msg = e.detail;
      setUnlockedMessages((prev) => {
        const next = new Map(prev);
        next.delete(Number(msg.id));
        return next;
      });
      notify({ type: "info", message: "Message hidden again." });
    };

    window.addEventListener("anachat-hide-message", onHideMsgEvent);
    window.addEventListener("anachat-unlock-message", onUnlockMsgEvent);
    window.addEventListener("anachat-unhide-message", onUnhideMsgEvent);
    window.addEventListener("anachat-change-key", onChangeKeyEvent);
    window.addEventListener("anachat-rehide-message", onRehideMsgEvent);

    return () => {
      window.removeEventListener("anachat-hide-message", onHideMsgEvent);
      window.removeEventListener("anachat-unlock-message", onUnlockMsgEvent);
      window.removeEventListener("anachat-unhide-message", onUnhideMsgEvent);
      window.removeEventListener("anachat-change-key", onChangeKeyEvent);
      window.removeEventListener("anachat-rehide-message", onRehideMsgEvent);
    };
  }, [loadMessages, notify]);

  // Request notification permission on first load
  useEffect(() => {
    if (!user?.id) return;
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {
          // Ignore permission errors
        });
      }
    } catch {
      // Ignore browser notification errors
    }
  }, [user?.id]);

  useEffect(() => {
    settingsRef.current = userSettings;
  }, [userSettings]);

  useEffect(() => {
    const raw = socket?.raw;
    if (!raw) return undefined;

    const onNotify = (payload = {}) => {
      const title = payload.title || "Notification";
      const message = payload.message || "";
      if (message) notify({ type: "info", title, message });
    };

    const onBroadcast = (payload = {}) => {
      const title = payload.title || "Announcement";
      const message = payload.message || "";
      if (message) notify({ type: "info", title, message });
    };

    raw.on("admin_notification", onNotify);
    raw.on("admin_broadcast", onBroadcast);
    return () => {
      raw.off("admin_notification", onNotify);
      raw.off("admin_broadcast", onBroadcast);
    };
  }, [notify, socket?.raw]);

  useEffect(() => {
    activeChatIdRef.current = activeChat?.id || null;
  }, [activeChat?.id]);

  useEffect(() => {
    setSelectedMessageIds({});
    setReplyToMessage(null);
    setTyping(false);
    setTypingName("");
    clearTimeout(typingTimer.current);
  }, [activeChat?.id]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    callRef.current = call;
  }, [call]);

  const selectChat = useCallback(async (chat) => {
    if (!chat) return;
    if (activeChat?.id !== chat.id) {
      setMessages([]);
      setTyping(false);
      setTypingName("");
    }
    setActiveChat(chat);
    await loadMessages(chat.id);
    void getChatRecipients(chat.id).catch(() => {});
    socket?.emit("join_room", chat.id);
    socket?.emit("seen", { chatId: chat.id });
    if (isMobile) setMobileChatOpen(true);
  }, [activeChat?.id, getChatRecipients, isMobile, socket]);

  // Ref tracking a pending message scroll request from hidden search navigation
  const pendingScrollMessageId = useRef(null);

  // Navigate to a hidden message from search results:
  // - If the message is in the current chat, scroll to it directly.
  // - If it's in a different chat, switch to that chat first, then scroll after messages load.
  const handleHiddenSearchNavigate = useCallback(async ({ chatId, messageId }) => {
    if (!chatId || !messageId) return;

    const scrollToMessage = (id) => {
      const el = document.getElementById(`msg-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.remove("reply-highlight-flash");
        void el.offsetWidth;
        el.classList.add("reply-highlight-flash");
      }
    };

    if (activeChatIdRef.current === Number(chatId)) {
      // Already in the right chat
      scrollToMessage(messageId);
    } else {
      // Switch chat, then scroll after messages are loaded
      pendingScrollMessageId.current = Number(messageId);
      const targetChat = chats.find(c => Number(c.id) === Number(chatId));
      if (targetChat) {
        await selectChat(targetChat);
      }
    }
  }, [chats, selectChat]);

  // Scroll to pending message once messages have loaded (after chat switch)
  useEffect(() => {
    if (!pendingScrollMessageId.current || loadingMessages) return;
    const id = pendingScrollMessageId.current;
    pendingScrollMessageId.current = null;
    // Small delay to allow DOM to render messages
    const t = setTimeout(() => {
      const el = document.getElementById(`msg-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.remove("reply-highlight-flash");
        void el.offsetWidth;
        el.classList.add("reply-highlight-flash");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [messages, loadingMessages]);

  const loadChats = useCallback(async (showSkeleton = false) => {
    if (showSkeleton && chats.length === 0) setLoadingChats(true);
    try {
      // 1. Load chats from local IndexedDB first for optimistic/local-first speed
      try {
        const localChats = await getLocalChats(user?.id);
        if (localChats && localChats.length > 0) {
          setChats(localChats);
        }
      } catch (err) {
        console.error("Failed to load local chats:", err);
      }

      const [{ data }, { data: hiddenCountData }] = await Promise.all([
        api.get("/chats"),
        api.get("/chats/hidden/count")
      ]);
      const list = sortChats(Array.isArray(data) ? data : []);
      const decryptedList = await Promise.all(list.map((chat) => decryptChatPreviewForMe(chat)));

      // 2. Save fetched/decrypted chats to local IndexedDB
      for (const chat of decryptedList) {
        try {
          await saveLocalChat(chat, user?.id);
        } catch (err) {
          console.error("Failed to save chat to local DB:", err);
        }
      }

      // 3. Retrieve final merged list from local IndexedDB
      const finalChats = await getLocalChats(user?.id);
      const serverChatIds = new Set(decryptedList.map((c) => c.id));
      for (const localChat of finalChats) {
        if (!serverChatIds.has(localChat.id)) {
          try {
            await deleteLocalChat(localChat.id, user?.id);
          } catch (delErr) {
            console.error("Failed to delete hidden/deleted chat from local DB:", delErr);
          }
        }
      }

      // Retrieve final list again after cleanup
      const cleanedChats = await getLocalChats(user?.id);
      setChats(cleanedChats.length > 0 ? cleanedChats : decryptedList);

      const nextHiddenCount = Number(hiddenCountData?.count || 0);
      setHiddenChatsCount(nextHiddenCount);
      if (!nextHiddenCount) {
        setHiddenChats([]);
        setChatPinCache("");
      }

      if (!finalChats.length && !decryptedList.length) {
        setActiveChat(null);
        setMessages([]);
        return;
      }

      const activeId = activeChatIdRef.current;
      if (!activeId) {
        return;
      }

      const stillActive = decryptedList.find((item) => item.id === activeId);
      if (stillActive) {
        setActiveChat((prev) => (prev?.id === stillActive.id ? { ...prev, ...stillActive } : stillActive));
      } else {
        setActiveChat(null);
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.message === "canceled" || err?.code === "ERR_CANCELED") {
        return; // Ignore canceled duplicate requests
      }
      setChats([]);
      setHiddenChats([]);
      setChatPinCache("");
      setHiddenChatsCount(0);
      notify({ type: "error", message: "Unable to load chats." });
    } finally {
      setLoadingChats(false);
    }
  }, [decryptChatPreviewForMe, isMobile, notify, selectChat, user?.id]);

  const handleAcceptRelationshipRequest = useCallback(async (requesterId) => {
    try {
      await api.post("/users/relationship-request/accept", { requesterId });
      notify({ type: "success", message: "Relationship request accepted! 💑" });
      fetchRelationshipStatus();
      await reload();
      await loadChats();
    } catch (err) {
      notify({ type: "error", message: err.response?.data?.message || "Failed to accept request" });
    }
  }, [fetchRelationshipStatus, loadChats, notify, reload]);

  const handleDeclineRelationshipRequest = useCallback(async (requesterId) => {
    try {
      await api.post("/users/relationship-request/decline", { requesterId });
      notify({ type: "info", message: "Relationship request declined" });
      fetchRelationshipStatus();
    } catch (err) {
      notify({ type: "error", message: "Failed to decline request" });
    }
  }, [fetchRelationshipStatus, notify]);

  const refreshActiveChatMessages = useCallback(async () => {
    if (!activeChatIdRef.current) return;
    const ok = await loadMessages(activeChatIdRef.current);
    if (ok) {
      notify({ type: "success", message: "Messages refreshed." });
    }
  }, [loadMessages, notify]);

  const applyMessagePatch = useCallback((messageId, patch) => {
    setMessages((prev) => prev.map((item) => (
      String(item.id) === String(messageId)
        ? {
            ...item,
            ...(typeof patch === "function" ? patch(item) : patch)
          }
        : item
    )));
  }, []);

  const saveUiSettings = useCallback(async (nextSettings) => {
    setSettingsSaving(true);
    try {
      if (
        nextSettings.notificationsEnabled
        && typeof Notification !== "undefined"
        && Notification.permission === "default"
      ) {
        try {
          await Notification.requestPermission();
        } catch {
          // ignore permission prompt failures
        }
      }

      const { data } = await api.patch("/users/me/settings", { settings: nextSettings });
      const merged = {
        ...DEFAULT_UI_SETTINGS,
        ...(data?.settings || nextSettings),
        compactMode: true
      };
      setUserSettings(merged);
      notify({ type: "success", message: "Settings saved." });
      await reload({ silent: true });
      setSettingsOpen(false);
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to save settings."
      });
    } finally {
      setSettingsSaving(false);
    }
  }, [notify, reload]);

  const handleCreateBackup = useCallback(async (pin) => {
    try {
      const dbDump = await exportLocalDbAsJson(user?.id);
      const encrypted = await encryptDatabaseBackup(dbDump, pin);
      const pinHash = await hashBackupPin(pin);

      const { data } = await api.post("/messages/backup", {
        backupBlob: encrypted.ciphertext,
        backupPinHash: pinHash,
        salt: encrypted.salt,
        iv: encrypted.iv
      });

      setBackupStatus("Enabled");
      setBackupSize(`${(data.size / 1024).toFixed(2)} KB`);
      setLastBackup(new Date(data.timestamp).toLocaleString());
      notify({ type: "success", message: "Cloud backup completed successfully!" });
    } catch (err) {
      notify({ type: "error", message: err.message || "Failed to create backup." });
      throw err;
    }
  }, [notify, user?.id]);

  const handleRestoreBackup = useCallback(async (pin) => {
    try {
      const { data } = await api.get("/messages/backup/download");
      const pinHash = await hashBackupPin(pin);
      
      if (data.backupPinHash !== pinHash) {
        throw new Error("Invalid backup PIN. Decryption rejected.");
      }

      const decryptedDb = await decryptDatabaseBackup(
        data.backupBlob,
        data.salt,
        data.iv,
        pin
      );

      await importLocalDbFromJson(decryptedDb, user?.id);
      
      // Reload chats and messages
      await loadChats();
      if (activeChatIdRef.current) {
        await loadMessages(activeChatIdRef.current);
      }

      notify({ type: "success", message: "Chats restored successfully!" });
    } catch (err) {
      notify({ type: "error", message: err.message || "Failed to restore backup." });
      throw err;
    }
  }, [loadChats, loadMessages, notify, user?.id]);

  const handleDisableBackup = useCallback(async () => {
    try {
      await api.delete("/messages/backup");
      setBackupStatus("Disabled");
      setBackupSize("N/A");
      setLastBackup("Never");
      setAutoBackupEnabled(false);
      sessionBackupKeyRef.current = null;
      notify({ type: "success", message: "Backups disabled and deleted from cloud." });
    } catch (err) {
      notify({ type: "error", message: "Failed to disable backup." });
      throw err;
    }
  }, [notify]);

  const handleEnableBackupWithPin = useCallback(async (pin) => {
    setBackupSetupBusy(true);
    try {
      // 1. Perform immediate backup with PIN
      await handleCreateBackup(pin);

      // 2. Set auto backup status to enabled on backend
      await api.patch("/messages/backup/settings", { enabled: true });
      setAutoBackupEnabled(true);
      setBackupStatus("Enabled");

      // 3. Cache the PIN in memory (or derive key) for automated background backups
      // We will derive the PBKDF2 key and cache the CryptoKey in sessionBackupKeyRef
      const dbDump = await exportLocalDbAsJson(user?.id);
      const keyObj = await hashBackupPin(pin); // simple hash wrapper for reuse or store raw string in session ref
      sessionBackupKeyRef.current = pin;

      setBackupSetupModalOpen(false);
      notify({ type: "success", message: "Auto Cloud Backup enabled successfully." });
    } catch (err) {
      notify({ type: "error", message: err.message || "Failed to enable auto backup." });
    } finally {
      setBackupSetupBusy(false);
    }
  }, [handleCreateBackup, notify, user?.id]);

  const handleRestoreBackupWithPin = useCallback(async (pin) => {
    setRestoreBackupBusy(true);
    try {
      await handleRestoreBackup(pin);
      // Cache PIN in memory for this session so auto backup works seamlessly
      sessionBackupKeyRef.current = pin;
      setRestoreBackupModalOpen(false);
    } catch (err) {
      // Throw back so modal can increment attempts and show error msg
      throw err;
    } finally {
      setRestoreBackupBusy(false);
    }
  }, [handleRestoreBackup]);

  useEffect(() => {
    async function checkBackupStatus() {
      try {
        const cachedPin = sessionStorage.getItem(`anach_backup_pin_${user?.id}`);
        if (cachedPin) {
          sessionBackupKeyRef.current = cachedPin;
        }

        const { data } = await api.get("/messages/backup/status");
        setLastBackupInfo(data);
        setAutoBackupEnabled(Boolean(data.autoBackupEnabled));
        if (data.hasBackup) {
          setBackupStatus(data.autoBackupEnabled ? "Enabled" : "Manual");
          setBackupSize(`${(data.lastBackupSize / 1024).toFixed(2)} KB`);
          setLastBackup(new Date(data.lastBackupAt).toLocaleString());
          
          try {
            const localChats = await getLocalChats(user?.id);
            if (localChats.length === 0) {
              if (sessionBackupKeyRef.current) {
                try {
                  await handleRestoreBackup(sessionBackupKeyRef.current);
                } catch (e) {
                  setRestoreBackupModalOpen(true);
                }
              } else {
                setRestoreBackupModalOpen(true);
              }
            }
          } catch (e) {
            console.warn("Could not check local chats for backup restore trigger:", e);
          }
        } else {
          setBackupStatus("Disabled");
          setBackupSize("N/A");
          setLastBackup("Never");
        }
      } catch (err) {
        console.error("Failed to check backup status", err);
      }
    }
    if (user?.id) {
      checkBackupStatus();
    }
  }, [user?.id, handleRestoreBackup]);

  // Debounced auto backup scheduler
  const autoBackupTimerRef = useRef(null);
  const triggerAutoBackup = useCallback(() => {
    if (!autoBackupEnabled || !sessionBackupKeyRef.current || !user?.id) return;
    
    if (autoBackupTimerRef.current) clearTimeout(autoBackupTimerRef.current);
    
    autoBackupTimerRef.current = setTimeout(async () => {
      try {
        console.log("[Backup] Auto-backup running in background...");
        const dbDump = await exportLocalDbAsJson(user.id);
        const encrypted = await encryptDatabaseBackup(dbDump, sessionBackupKeyRef.current);
        const pinHash = await hashBackupPin(sessionBackupKeyRef.current);

        const { data } = await api.post("/messages/backup", {
          backupBlob: encrypted.ciphertext,
          backupPinHash: pinHash,
          salt: encrypted.salt,
          iv: encrypted.iv
        });

        setBackupSize(`${(data.size / 1024).toFixed(2)} KB`);
        setLastBackup(new Date(data.timestamp).toLocaleString());
        console.log("[Backup] Auto-backup completed successfully.");
      } catch (err) {
        console.error("Auto backup failed in background:", err);
      }
    }, 5 * 60 * 1000); // 5 minutes debounce
  }, [autoBackupEnabled, user?.id]);

  const handleLogout = useCallback(async () => {
    if (user?.id) {
      try {
        await clearLocalDb(user.id);
      } catch (err) {
        console.error("Failed to clear local db on logout:", err);
      }
    }
    // Remove local storage keys for specific scopes
    try {
      localStorage.removeItem(`ana_pinned_chats_v1_${user?.id || "guest"}`);
    } catch (e) {}

    // Reset local UI states
    setChats([]);
    setMessages([]);
    setActiveChat(null);
    setUnlockedMessages(new Map());
    setUnreadByChat({});
    setPinnedChatIds([]);

    logout();
  }, [logout, user?.id]);

  const createChat = useCallback(async (otherUserId) => {
    try {
      const { data } = await api.post("/chats", { otherUserId });
      const { data: latest } = await api.get("/chats");
      const list = sortChats(Array.isArray(latest) ? latest : []);
      const decryptedList = await Promise.all(list.map((chat) => decryptChatPreviewForMe(chat)));
      setChats(decryptedList);
      const target = decryptedList.find((chat) => chat.id === data?.id) || decryptedList[0];
      if (target) await selectChat(target);
      setSearch("");
    } catch (err) {
      notify({ type: "error", message: err.response?.data?.message || "Unable to start chat." });
    }
  }, [decryptChatPreviewForMe, notify, selectChat, setSearch]);

  const openGroupCreator = useCallback(async () => {
    setGroupModalOpen(true);
    setGroupUsersLoading(true);
    try {
      const { data } = await api.get("/users", { params: { q: "" } });
      setGroupUsers(Array.isArray(data) ? data : []);
    } catch {
      setGroupUsers([]);
      notify({ type: "error", message: "Unable to load users for group." });
    } finally {
      setGroupUsersLoading(false);
    }
  }, [notify]);

  const createGroup = useCallback(async ({ name, memberIds }) => {
    setGroupCreating(true);
    try {
      const { data } = await api.post("/chats/group", { name, memberIds });
      const createdChatId = Number(data?.id);
      const { data: latest } = await api.get("/chats");
      const list = sortChats(Array.isArray(latest) ? latest : []);
      const decryptedList = await Promise.all(list.map((chat) => decryptChatPreviewForMe(chat)));
      setChats(decryptedList);

      const target = decryptedList.find((chat) => Number(chat.id) === createdChatId) || decryptedList[0];
      if (target) await selectChat(target);
      setGroupModalOpen(false);
      notify({ type: "success", message: "Group created." });
    } catch (err) {
      notify({ type: "error", message: err.response?.data?.message || "Unable to create group." });
    } finally {
      setGroupCreating(false);
    }
  }, [decryptChatPreviewForMe, notify, selectChat]);

  const unlockHiddenChatsWithPin = useCallback(async (rawPin, { silent = false } = {}) => {
    const pin = (rawPin || "").toString().trim();
    if (!CHAT_PIN_PATTERN.test(pin)) {
      if (!silent) notify({ type: "error", message: "PIN must be 4 to 8 digits." });
      return { ok: false };
    }

    try {
      const { data } = await api.post("/chats/hidden/unlock", { pin });
      const list = sortChats(Array.isArray(data?.chats) ? data.chats : []);
      const decryptedList = await Promise.all(list.map((chat) => decryptChatPreviewForMe(chat)));
      setHiddenChats(decryptedList);
      setHiddenChatsCount(decryptedList.length);
      setChatPinCache(pin);
      if (!silent) {
        notify({ type: "success", message: decryptedList.length ? "Hidden chats unlocked." : "No hidden chats found." });
      }
      return { ok: true, list: decryptedList };
    } catch (err) {
      if (!silent) {
        notify({
          type: "error",
          message: err.response?.data?.message || "Unable to unlock hidden chats."
        });
      }
      return { ok: false };
    }
  }, [decryptChatPreviewForMe, notify]);

  const hideActiveChat = useCallback(async () => {
    if (!activeChat?.id) return;
    if (activeChat.chat_type === "self") {
      notify({ type: "info", message: "AnaLocker chat cannot be hidden." });
      return;
    }

    setModalConfig({
      type: "prompt",
      title: "Set Chat PIN",
      message: "Enter 4-8 digit PIN. First time PIN will be set for hidden chats.",
      placeholder: "Enter PIN",
      isPassword: true,
      onConfirm: async (pinInput) => {
        const pin = (pinInput || "").trim();
        if (!pin) return;
        try {
          const { data } = await api.post(`/chats/${activeChat.id}/hide`, { pin });
          if (data?.pin_created) {
            notify({ type: "success", message: "Chat hidden. PIN created successfully." });
          } else {
            notify({ type: "success", message: "Chat hidden with PIN." });
          }
          await deleteLocalChat(activeChat.id, user?.id).catch(() => {});
          await unlockHiddenChatsWithPin(pin, { silent: true });
          setActiveChat(null);
          setMessages([]);
          await loadChats();
        } catch (err) {
          notify({
            type: "error",
            message: err.response?.data?.message || "Unable to hide chat."
          });
        }
      }
    });
  }, [activeChat, loadChats, notify, unlockHiddenChatsWithPin]);

  const unhideChat = useCallback(async (chatId) => {
    const targetChatId = Number(chatId);
    if (!targetChatId) return;

    const performUnhide = async (pinValue) => {
      try {
        await api.post(`/chats/${targetChatId}/unhide`, { pin: pinValue });
        await unlockHiddenChatsWithPin(pinValue, { silent: true });
        notify({ type: "success", message: "Chat unhidden." });
        await loadChats();
      } catch (err) {
        setChatPinCache("");
        notify({
          type: "error",
          message: err.response?.data?.message || "Unable to unhide chat."
        });
      }
    };

    let pin = chatPinCache;
    if (!pin) {
      setModalConfig({
        type: "prompt",
        title: "Unhide Chat",
        message: "Enter chat PIN to unhide this chat",
        placeholder: "Enter PIN",
        isPassword: true,
        onConfirm: (pinInput) => {
          const pinVal = (pinInput || "").trim();
          if (!pinVal) return;
          performUnhide(pinVal);
        }
      });
    } else {
      await performUnhide(pin);
    }
  }, [chatPinCache, loadChats, notify, unlockHiddenChatsWithPin]);

  const setAndPersistPinnedChatIds = useCallback((updater) => {
    setPinnedChatIds((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const normalized = Array.isArray(next) ? next.map((id) => Number(id)).filter(Boolean) : [];
      try {
        localStorage.setItem(`ana_pinned_chats_v1_${user?.id || "guest"}`, JSON.stringify(normalized));
      } catch {
        // ignore
      }
      return normalized;
    });
  }, [user?.id]);

  const togglePinChat = useCallback((chatId) => {
    const id = Number(chatId);
    if (!id) return;
    setAndPersistPinnedChatIds((prev) => {
      const set = new Set((prev || []).map((v) => Number(v)).filter(Boolean));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });
  }, [setAndPersistPinnedChatIds]);

  const hideChatById = useCallback(async (chatId) => {
    const id = Number(chatId);
    if (!id) return;

    const target = chatsRef.current.find((c) => Number(c.id) === id) || null;
    if (target?.chat_type === "self") {
      notify({ type: "info", message: "AnaLocker chat cannot be hidden." });
      return;
    }

    setModalConfig({
      type: "prompt",
      title: "Set Chat PIN",
      message: "Enter 4-8 digit PIN. First time PIN will be set for hidden chats.",
      placeholder: "Enter PIN",
      isPassword: true,
      onConfirm: async (pinInput) => {
        const pin = (pinInput || "").trim();
        if (!pin) return;
        try {
          const { data } = await api.post(`/chats/${id}/hide`, { pin });
          if (data?.pin_created) notify({ type: "success", message: "Chat hidden. PIN created successfully." });
          else notify({ type: "success", message: "Chat hidden with PIN." });
          await deleteLocalChat(id, user?.id).catch(() => {});
          await unlockHiddenChatsWithPin(pin, { silent: true });
          if (Number(activeChat?.id) === id) {
            setActiveChat(null);
            setMessages([]);
          }
          await loadChats();
        } catch (err) {
          notify({
            type: "error",
            message: err.response?.data?.message || "Unable to hide chat."
          });
        }
      }
    });
  }, [loadChats, notify, unlockHiddenChatsWithPin, activeChat?.id, user?.id]);

  const deleteChatById = useCallback(async (chatId) => {
    const id = Number(chatId);
    if (!id) return;
    const target = chatsRef.current.find((c) => Number(c.id) === id) || null;
    if (target?.chat_type === "self") {
      notify({ type: "info", message: "AnaLocker chat cannot be deleted." });
      return;
    }

    setModalConfig({
      type: "confirm",
      title: "Delete Chat",
      message: "Are you sure you want to permanently delete this chat? This will clear all messages and remove it from your chat list.",
      onConfirm: async () => {
        try {
          await api.delete(`/chats/${id}`);
          await clearLocalMessagesForChat(id, user?.id).catch(() => {});
          await deleteLocalChat(id, user?.id).catch(() => {});
          notify({ type: "success", message: "Chat deleted successfully." });
          if (Number(activeChat?.id) === id) {
            setActiveChat(null);
            setMessages([]);
          }
          await loadChats();
        } catch (err) {
          notify({
            type: "error",
            message: err.response?.data?.message || "Unable to delete chat."
          });
        }
      }
    });
  }, [activeChat?.id, loadChats, notify, user?.id]);

  const updateBlockStateForUser = useCallback((targetUserId, blockedByMe) => {
    if (!targetUserId) return;
    const nextValue = blockedByMe ? 1 : 0;
    setChats((prev) => prev.map((chat) => {
      if (chat.chat_type === "group") return chat;
      if (Number(chat.other_user_id) !== Number(targetUserId)) return chat;
      return { ...chat, blocked_by_me: nextValue };
    }));
    setActiveChat((prev) => {
      if (!prev || prev.chat_type === "group") return prev;
      if (Number(prev.other_user_id) !== Number(targetUserId)) return prev;
      return { ...prev, blocked_by_me: nextValue };
    });
  }, []);

  const blockActiveUser = useCallback(() => {
    const targetUserId = Number(activeChat?.other_user_id);
    if (!targetUserId || activeChat?.chat_type === "group" || activeChat?.chat_type === "self" || blockActionBusy) return;
    setModalConfig({
      type: "confirm",
      title: "Block User",
      message: "Block this user? You will not be able to send messages or calls until you unblock.",
      onConfirm: async () => {
        setBlockActionBusy(true);
        try {
          await api.post(`/users/${targetUserId}/block`);
          updateBlockStateForUser(targetUserId, true);
          notify({ type: "success", message: "User blocked." });
        } catch (err) {
          notify({
            type: "error",
            message: err.response?.data?.message || "Unable to block user."
          });
        } finally {
          setBlockActionBusy(false);
        }
      }
    });
  }, [activeChat?.chat_type, activeChat?.other_user_id, blockActionBusy, notify, updateBlockStateForUser]);

  const unblockActiveUser = useCallback(async () => {
    const targetUserId = Number(activeChat?.other_user_id);
    if (!targetUserId || activeChat?.chat_type === "group" || activeChat?.chat_type === "self" || blockActionBusy) return;
    setBlockActionBusy(true);
    try {
      await api.delete(`/users/${targetUserId}/block`);
      updateBlockStateForUser(targetUserId, false);
      notify({ type: "success", message: "User unblocked." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to unblock user."
      });
    } finally {
      setBlockActionBusy(false);
    }
  }, [activeChat?.chat_type, activeChat?.other_user_id, blockActionBusy, notify, updateBlockStateForUser]);

  const reportUser = useCallback(async ({ userId, reason, details }) => {
    const targetUserId = Number(userId);
    if (!targetUserId) return;
    try {
      await api.post(`/users/${targetUserId}/report`, {
        reason,
        details
      });
      notify({ type: "success", message: "Report submitted. Our team will review it." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to submit report."
      });
      throw err;
    }
  }, [notify]);

  const setChatBackground = useCallback(async ({ preset, file }) => {
    const chatId = Number(activeChatIdRef.current);
    if (!chatId) return;

    const form = new FormData();
    if (preset) form.append("backgroundPreset", preset);
    if (file) form.append("background", file);
    if (!preset && !file) return;

    try {
      const { data } = await api.patch(`/chats/${chatId}/background`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const nextBackground = data?.chat_background_url || null;
      setChats((prev) => prev.map((chat) => (
        Number(chat.id) === chatId ? { ...chat, chat_background_url: nextBackground } : chat
      )));
      setActiveChat((prev) => (
        prev && Number(prev.id) === chatId ? { ...prev, chat_background_url: nextBackground } : prev
      ));
      notify({ type: "success", message: "Chat background updated." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to update chat background."
      });
      throw err;
    }
  }, [notify]);

  const clearChatBackground = useCallback(async () => {
    const chatId = Number(activeChatIdRef.current);
    if (!chatId) return;
    try {
      const { data } = await api.patch(`/chats/${chatId}/background`, { clear: true });
      const nextBackground = data?.chat_background_url || null;
      setChats((prev) => prev.map((chat) => (
        Number(chat.id) === chatId ? { ...chat, chat_background_url: nextBackground } : chat
      )));
      setActiveChat((prev) => (
        prev && Number(prev.id) === chatId ? { ...prev, chat_background_url: nextBackground } : prev
      ));
      notify({ type: "success", message: "Chat background removed." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to reset chat background."
      });
      throw err;
    }
  }, [notify]);

  const pauseToastTimer = useCallback((id) => {
    const timer = toastTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.current.delete(id);
    }
  }, []);

  const sendMessageToChat = useCallback(async (chatId, body) => {
    const targetChat = chatsRef.current.find((c) => Number(c.id) === Number(chatId));
    if (!targetChat) return;

    if (targetChat.chat_type !== "group") {
      if (targetChat.blocked_by_me || targetChat.blocked_me) return;
    }

    const clientMessageId = createClientMessageId();
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      chat_id: targetChat.id,
      sender_id: user.id,
      client_message_id: clientMessageId,
      body: body || null,
      image_url: null,
      message_type: "text",
      seen: 0,
      delivery_status: "sending",
      created_at: new Date().toISOString(),
      pending: true
    };

    if (activeChatIdRef.current === targetChat.id) {
      setMessages((prev) => [...prev, optimistic]);
    }
    void saveLocalMessage(optimistic, user?.id).catch(err => console.error("Error saving optimistic message:", err));

    setChats((prev) => {
      const next = prev.map((chat) => (
        chat.id === targetChat.id
          ? {
              ...chat,
              last_message_at: optimistic.created_at,
              last_message_body: body,
              last_message_image: null
            }
          : chat
      ));
      const sorted = sortChats(next);
      const updatedChat = sorted.find(c => c.id === targetChat.id);
      if (updatedChat) saveLocalChat(updatedChat, user?.id).catch(err => console.error(err));
      return sorted;
    });

    const form = new FormData();
    form.append("chatId", String(targetChat.id));
    form.append("clientMessageId", clientMessageId);
    form.append("messageType", "text");

    try {
      const recipients = await getChatRecipients(targetChat.id);
      const { e2ee } = await encryptOutgoingMessage({
        plaintext: body || "",
        file: null,
        recipients
      });

      form.append("e2ee", JSON.stringify(e2ee));

      const { data } = await api.post("/messages", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const decryptedData = await decryptMessageForMe(data);

      if (activeChatIdRef.current === targetChat.id) {
        setMessages((prev) => {
          const replaced = prev.map((msg) => (msg.id === tempId ? { ...decryptedData, delivery_status: "sent" } : msg));
          return replaced;
        });
      }
      deleteLocalMessage(tempId, user?.id).catch(() => {});
      saveLocalMessage({ ...decryptedData, delivery_status: "sent" }, user?.id).catch(err => console.error(err));
    } catch (err) {
      console.error("Failed to send message to chat from notification:", err);
    }
  }, [decryptMessageForMe, getChatRecipients, user?.id]);

  const sendMessage = useCallback(async ({ body, media, replyToMessageId, messageType = "text" }) => {
    if (!activeChat?.id) return;
    if (activeChat.chat_type !== "group") {
      if (activeChat.blocked_by_me) {
        const error = new Error("You blocked this user. Unblock to send messages.");
        error.response = { data: { message: error.message } };
        throw error;
      }
      if (activeChat.blocked_me) {
        const error = new Error("This user blocked you. Message cannot be sent.");
        error.response = { data: { message: error.message } };
        throw error;
      }
    }
    const clientMessageId = createClientMessageId();
    const replyTarget = replyToMessageId
      ? (messages.find((item) => Number(item.id) === Number(replyToMessageId)) || replyToMessage)
      : null;
    const replySenderName = replyTarget
      ? (replyTarget.sender_name
        || (Number(replyTarget.sender_id) === Number(user.id) ? "You" : (activeChat.other_user_name || "User")))
      : null;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      chat_id: activeChat.id,
      sender_id: user.id,
      client_message_id: clientMessageId,
      body: body || null,
      image_url: null,
      reply_to_message_id: replyTarget?.id || null,
      reply_to_sender_id: replyTarget?.sender_id || null,
      reply_to_sender_name: replySenderName,
      reply_to_body: replyTarget?.body || null,
      reply_to_image_url: replyTarget?.image_url || null,
      reply_to_deleted_for_everyone: Boolean(replyTarget?.deleted_for_everyone),
      message_type: messageType,
      poll_votes: messageType === "poll" ? {} : undefined,
      seen: 0,
      delivery_status: "sending",
      created_at: new Date().toISOString(),
      pending: true
    };

    setMessages((prev) => [...prev, optimistic]);
    void saveLocalMessage(optimistic, user?.id).catch(err => console.error("Error saving optimistic message:", err));

    setChats((prev) => {
      const next = prev.map((chat) => (
        chat.id === activeChat.id
          ? {
              ...chat,
              last_message_at: optimistic.created_at,
              last_message_body: body || (media ? "[media]" : ""),
              last_message_image: media ? "media" : null
            }
          : chat
      ));
      const sorted = sortChats(next);
      const updatedChat = sorted.find(c => c.id === activeChat.id);
      if (updatedChat) saveLocalChat(updatedChat, user?.id).catch(err => console.error("Error saving chat local:", err));
      return sorted;
    });

    const form = new FormData();
    form.append("chatId", String(activeChat.id));
    form.append("clientMessageId", clientMessageId);
    form.append("messageType", messageType);
    if (replyTarget?.id) form.append("replyToMessageId", String(replyTarget.id));

    try {
      const recipients = await getChatRecipients(activeChat.id);
      const { e2ee, encryptedFile } = await encryptOutgoingMessage({
        plaintext: body || "",
        file: media || null,
        recipients
      });

      form.append("e2ee", JSON.stringify(e2ee));
      if (encryptedFile) {
        form.append("media", encryptedFile, "encrypted.bin");
      }

      const { data } = await api.post("/messages", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const decryptedData = await decryptMessageForMe(data);

      setMessages((prev) => {
        const replaced = prev.map((msg) => (msg.id === tempId ? { ...decryptedData, delivery_status: "sent" } : msg));
        const final = replaced.some((msg) => msg.id === decryptedData.id) ? replaced : [...replaced, { ...decryptedData, delivery_status: "sent" }];
        // Remove temporary message and put final E2EE message
        deleteLocalMessage(tempId, user?.id).catch(() => {});
        saveLocalMessage({ ...decryptedData, delivery_status: "sent" }, user?.id).catch(err => console.error(err));
        return final;
      });
      setChats((prev) => {
        const next = prev.map((chat) => (
          chat.id === activeChat.id
            ? {
                ...chat,
                last_message_at: decryptedData.created_at,
                last_message_body: decryptedData.body,
                last_message_image: decryptedData.image_url
              }
            : chat
        ));
        const sorted = sortChats(next);
        const updatedChat = sorted.find(c => c.id === activeChat.id);
        if (updatedChat) saveLocalChat(updatedChat, user?.id).catch(err => console.error("Error saving chat local:", err));
        return sorted;
      });
      setReplyToMessage(null);
      
      // Trigger background auto backup check
      triggerAutoBackup();
    } catch (err) {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      deleteLocalMessage(tempId, user?.id).catch(() => {});
      throw err;
    }
  }, [activeChat, decryptMessageForMe, getChatRecipients, messages, replyToMessage, user?.id, triggerAutoBackup]);

  const sendCallChatMessage = useCallback(async (text) => {
    const chatId = callRef.current.chatId;
    const toUserId = callRef.current.peerUserId;
    const body = (text || "").trim();
    if (!chatId || !body || !toUserId) return;

    const tempId = `call-temp-${Date.now()}-${Math.random()}`;
    const newMsg = {
      id: tempId,
      sender_id: user?.id,
      body,
      created_at: new Date().toISOString()
    };

    setCallChatMessages((prev) => [...prev, newMsg]);

    if (socket && socket.emit) {
      socket.emit("call_chat_message", {
        toUserId,
        body,
        chatId
      });
    }
  }, [socket, user?.id]);

  const removeLocalMessage = useCallback(async (message) => {
    if (!message?.id) return;
    try {
      await api.post(`/messages/${message.id}/delete-for-me`, { chatId: message.chat_id });
      await deleteLocalMessage(message.id, user?.id).catch(() => {});
      setMessages((prev) => prev.filter((item) => item.id !== message.id));
      notify({ type: "success", message: "Message deleted for you." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to delete message."
      });
    }
  }, [notify, user?.id]);

  const editMessage = useCallback(async (message, nextBody) => {
    if (!message?.id) return;
    const text = (nextBody || "").trim();
    if (!text || text === (message.body || "").trim()) return;
    try {
      const chatId = Number(message.chat_id);
      if (!chatId) throw new Error("chat_id missing on message");

      const recipients = await getChatRecipients(chatId);
      const { e2ee } = await encryptOutgoingMessage({ plaintext: text, file: null, recipients });

      const { data } = await api.patch(`/messages/${message.id}/edit`, { chatId, e2ee });
      const decrypted = await decryptMessageForMe({ ...message, ...(data?.message || {}), e2ee });
      applyMessagePatch(message.id, decrypted);
      await saveLocalMessage(decrypted, user?.id).catch(() => {});
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to edit message."
      });
    }
  }, [applyMessagePatch, decryptMessageForMe, getChatRecipients, notify, user?.id]);

  const handleTogglePin = useCallback(async (message) => {
    if (!activeChat?.id) return;
    const isCurrentlyPinned = activeChat.pinned_message_id === message.id;
    try {
      if (isCurrentlyPinned) {
        await api.delete(`/chats/${activeChat.id}/pin-message`);
        setActiveChat(prev => prev ? { ...prev, pinned_message_id: null } : null);
        setChats(prev => prev.map(c => c.id === activeChat.id ? { ...c, pinned_message_id: null } : c));
      } else {
        await api.post(`/chats/${activeChat.id}/pin-message`, { messageId: message.id });
        setActiveChat(prev => prev ? { ...prev, pinned_message_id: message.id } : null);
        setChats(prev => prev.map(c => c.id === activeChat.id ? { ...c, pinned_message_id: message.id } : c));
      }
    } catch (err) {
      notify?.({
        type: "error",
        title: isCurrentlyPinned ? "Failed to unpin message" : "Failed to pin message",
        message: err?.response?.data?.message || err?.message || "Operation failed."
      });
    }
  }, [activeChat, notify]);

  const handleVotePoll = useCallback(async (messageId, optionIndex) => {
    if (!activeChat?.id) return;
    if (socket && socket.emit) {
      socket.emit("vote_poll", { messageId, chatId: activeChat.id, optionIndex });
    }
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              poll_votes: {
                ...(msg.poll_votes || {}),
                [user.id]: optionIndex
              }
            }
          : msg
      )
    );
    try {
      await api.post(`/messages/${messageId}/vote`, { optionIndex });
    } catch (err) {
      console.error("Failed to persist vote:", err);
    }
  }, [activeChat?.id, socket, user?.id]);

  const deleteForEveryone = useCallback(async (message) => {
    if (!message?.id) return;
    try {
      const patch = {
        body: null,
        image_url: null,
        e2ee: null,
        deleted_for_everyone: true
      };
      await api.post(`/messages/${message.id}/delete-for-everyone`, { chatId: message.chat_id });
      applyMessagePatch(message.id, patch);
      await saveLocalMessage({ ...message, ...patch }, user?.id).catch(() => {});
      await loadChats();
      notify({ type: "success", message: "Message deleted for everyone." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to delete for everyone."
      });
    }
  }, [applyMessagePatch, notify, user?.id, loadChats]);

  const toggleStarMessage = useCallback(async (message) => {
    if (!message?.id) return;
    const nextStarred = !message.my_starred;
    try {
      await api.post(`/messages/${message.id}/star`, { starred: nextStarred });
      applyMessagePatch(message.id, { my_starred: nextStarred });
      notify({ type: "success", message: nextStarred ? "Message starred." : "Message unstarred." });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to update star."
      });
    }
  }, [applyMessagePatch, notify]);

  const reactToMessage = useCallback(async (message, reaction) => {
    if (!message?.id) return;
    try {
      const { data } = await api.post(`/messages/${message.id}/react`, {
        reaction,
        chatId: message.chat_id || message.chatId
      });
      applyMessagePatch(message.id, {
        my_reaction: data.my_reaction || null,
        reactions: data.reactions || {}
      });
    } catch (err) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Unable to react on message."
      });
    }
  }, [applyMessagePatch, notify]);

  const forwardMessage = useCallback(async (msgOrArray) => {
    const messagesArray = Array.isArray(msgOrArray) ? msgOrArray : [msgOrArray];
    if (!messagesArray.length) return;
    const firstMessage = messagesArray[0];
    if (!firstMessage?.id) return;
    if (!firstMessage.e2ee?.key) {
      notify({ type: "error", message: "This message cannot be forwarded (missing encryption envelope)." });
      return;
    }
    const candidates = chats.filter((chat) => (
      chat.id !== activeChatIdRef.current
      && (chat.chat_type === "group" || (!chat.blocked_by_me && !chat.blocked_me))
    ));
    if (!candidates.length) {
      notify({ type: "info", message: "No eligible chat available for forwarding." });
      return;
    }

    setModalConfig({
      type: "forward",
      title: messagesArray.length > 1 ? "Forward Messages" : "Forward Message",
      message: messagesArray.length > 1 
        ? `Select chats to forward the ${messagesArray.length} messages to:` 
        : "Select chats to forward the message to:",
      candidates,
      onConfirm: async (targetChatIds) => {
        try {
          const targetIds = Array.isArray(targetChatIds) ? targetChatIds : [targetChatIds];
          const pair = await getOrCreateRsaKeyPair(user.id);
          
          for (const targetChatId of targetIds) {
            const recipients = await getChatRecipients(targetChatId);
            for (const message of messagesArray) {
              const keys = await reencryptAesKeyForRecipients({
                e2ee: message.e2ee,
                privateJwk: pair.privateJwk,
                recipients
              });
              await api.post(`/messages/${message.id}/forward`, { targetChatId, keys });
            }
          }

          notify({ 
            type: "success", 
            message: messagesArray.length > 1 ? `${messagesArray.length} messages forwarded.` : "Message forwarded." 
          });
          setSelectedMessageIds({});
          await loadChats();
        } catch (err) {
          notify({
            type: "error",
            message: err.response?.data?.message || "Unable to forward message(s)."
          });
        }
      }
    });
  }, [chats, getChatRecipients, loadChats, notify, user.id]);

  const toggleSelectMessage = useCallback((message) => {
    if (!message?.id) return;
    setSelectedMessageIds((prev) => {
      const next = { ...prev };
      if (next[message.id]) delete next[message.id];
      else next[message.id] = true;
      return next;
    });
  }, []);

  const handleReply = useCallback((message) => {
    if (!message?.id) return;
    setReplyToMessage({
      id: message.id,
      sender_id: message.sender_id,
      sender_name: message.sender_name
        || (Number(message.sender_id) === Number(user.id) ? "You" : (activeChat?.other_user_name || "User")),
      body: message.body || null,
      image_url: message.image_url || null,
      deleted_for_everyone: Boolean(message.deleted_for_everyone)
    });
  }, [activeChat?.other_user_name, user?.id]);

  const handleSeen = useCallback(() => {
    if (!activeChatIdRef.current) return;
    socket?.emitWithMonitoring?.("seen", { chatId: activeChatIdRef.current }) || socket?.emit("seen", { chatId: activeChatIdRef.current });
  }, [socket]);

  const setWatchSource = useCallback(async ({ chatId, sourceUrl, title }) => {
    if (!socket) {
      notify({ type: "error", message: "Realtime connection is unavailable." });
      return;
    }
    const normalizedChatId = Number(chatId || activeChatIdRef.current);
    if (!normalizedChatId) return;

    socket.emit("watch_session_set", {
      chatId: normalizedChatId,
      sourceUrl: (sourceUrl || "").toString().trim(),
      title: (title || "").toString().trim()
    });
  }, [notify, socket]);

  const clearWatchSession = useCallback(async ({ chatId }) => {
    if (!socket) return;
    const normalizedChatId = Number(chatId || activeChatIdRef.current);
    if (!normalizedChatId) return;
    socket.emit("watch_session_clear", { chatId: normalizedChatId });
  }, [socket]);

  const syncWatchPlayback = useCallback(({ chatId, action, position, playbackRate, isPlaying }) => {
    if (!socket) return;
    const normalizedChatId = Number(chatId || activeChatIdRef.current);
    if (!normalizedChatId) return;
    socket.emit("watch_playback_sync", {
      chatId: normalizedChatId,
      action,
      position,
      playbackRate,
      isPlaying
    });
  }, [socket]);

  const searchQuery = search.trim().toLowerCase();
  const filteredChats = useMemo(() => {
    if (!searchQuery) return chats;
    return chats.filter((chat) => {
      const name = (chat.other_user_name || "").toLowerCase();
      const last = (chat.last_message_body || "").toLowerCase();
      const mobile = (chat.other_user_mobile || "").toLowerCase();
      return name.includes(searchQuery) || last.includes(searchQuery) || mobile.includes(searchQuery);
    });
  }, [chats, searchQuery]);

  const orderedFilteredChats = useMemo(() => {
    const list = Array.isArray(filteredChats) ? filteredChats : [];
    const lockerChat = list.find((c) => c.chat_type === "self");
    const remainingList = list.filter((c) => c.chat_type !== "self");

    // Sort remainingList by latest message time
    remainingList.sort((a, b) => {
      const left = new Date(b.last_message_at || b.last_message_created_at || 0).getTime();
      const right = new Date(a.last_message_at || a.last_message_created_at || 0).getTime();
      return left - right;
    });

    const pinnedSet = new Set(pinnedChatIds.map((id) => Number(id)).filter(Boolean));
    const pinned = [];
    const others = [];
    remainingList.forEach((chat) => {
      if (pinnedSet.has(Number(chat.id))) pinned.push(chat);
      else others.push(chat);
    });

    if (lockerChat) {
      return [lockerChat, ...pinned, ...others];
    }
    return [...pinned, ...others];
  }, [filteredChats, pinnedChatIds]);

  useEffect(() => {
    loadChats(true);
  }, [loadChats]);

  useEffect(() => {
    const handleChatsUpdated = () => {
      loadChats(false);
    };
    const handleChatCleared = (e) => {
      const clearedChatId = Number(e.detail?.chatId);
      if (clearedChatId) {
        void clearLocalMessagesForChat(clearedChatId, user?.id).catch(() => {});
      }
      if (clearedChatId && activeChatIdRef.current === clearedChatId) {
        setMessages([]);
      }
    };
    window.addEventListener("ana_chats_updated", handleChatsUpdated);
    window.addEventListener("ana_active_chat_cleared", handleChatCleared);
    return () => {
      window.removeEventListener("ana_chats_updated", handleChatsUpdated);
      window.removeEventListener("ana_active_chat_cleared", handleChatCleared);
    };
  }, [loadChats, user?.id]);

  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) {
      setPeopleResults([]);
      return undefined;
    }

    let cancelled = false;
    setSearchingPeople(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", { params: { q: search.trim() } });
        if (cancelled) return;
        const existingIds = new Set(chatsRef.current.map((chat) => chat.other_user_id));
        const list = Array.isArray(data) ? data.filter((person) => !existingIds.has(person.id)) : [];
        setPeopleResults(list.slice(0, 8));
      } catch {
        if (!cancelled) setPeopleResults([]);
      } finally {
        if (!cancelled) setSearchingPeople(false);
      }
    }, 240);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    const pin = search.trim();
    if (!hiddenChatsCount) return undefined;
    if (!CHAT_PIN_PATTERN.test(pin)) return undefined;
    if (pin === chatPinCache && hiddenChats.length > 0) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const res = await unlockHiddenChatsWithPin(pin, { silent: true });
      if (res && res.ok) {
        setSearch(""); // Automatically clear the search bar PIN input to restore chat list structure
      }
    }, 260);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, hiddenChatsCount, chatPinCache, hiddenChats.length, unlockHiddenChatsWithPin]);

  const patchActiveCallLog = useCallback((patch) => {
    const id = callRef.current?.logId || callLogIdRef.current;
    if (!id) return;
    patchCallLog(user.id, id, patch || {});
  }, [user.id]);

  const finalizeActiveCallLog = useCallback((status, reason) => {
    const id = callRef.current?.logId || callLogIdRef.current;
    if (!id) return;
    patchCallLog(user.id, id, {
      status: status || "ended",
      reason: reason || "ended",
      ended_at: new Date().toISOString()
    });
    callLogIdRef.current = null;
  }, [user.id]);

  const resetCallState = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.onicecandidate = null;
      peerRef.current.ontrack = null;
      peerRef.current.onconnectionstatechange = null;
      peerRef.current.close();
      peerRef.current = null;
    }
    stopStream(screenStreamRef.current);
    stopStream(localStreamRef.current);
    stopStream(remoteStreamRef.current);
    screenStreamRef.current = null;
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    incomingOfferRef.current = null;
    audioSenderRef.current = null;
    videoSenderRef.current = null;
    cameraTrackRef.current = null;
    setLocalCallStream(null);
    setRemoteCallStream(null);
    setPeerConn(null);
    setCall(IDLE_CALL);
    setCallChatMessages([]);
  }, []);

  const endCall = useCallback((notifyPeer = true, reason = "ended") => {
    const current = callRef.current;
    if (notifyPeer && socket && current.peerUserId) {
      socket.emit("call_end", {
        toUserId: current.peerUserId,
        chatId: current.chatId,
        reason
      });
    }
    finalizeActiveCallLog(reason === "ended" ? "ended" : reason, reason);

    // Capture snapshot for CallSummary before resetting
    if (ENABLE_CALL_SUMMARY && current.phase === "active") {
      const logId     = callRef.current?.logId || callLogIdRef.current;
      const logs      = logId ? getCallLogs(user.id) : [];
      const latestLog = logs.find((l) => l.id === logId) || {};

      const startMs  = current.startedAt ? new Date(current.startedAt).getTime() : NaN;
      const now      = Date.now();
      const totalSec = Number.isFinite(startMs) ? Math.round((now - startMs) / 1000) : 0;
      const mins     = Math.floor(totalSec / 60);
      const secs     = totalSec % 60;
      const durationStr = totalSec > 0 ? `${mins}m ${String(secs).padStart(2, "0")}s` : null;

      callSnapshotRef.current = {
        peerName:         current.peerName  || "Unknown",
        peerAvatar:       current.peerAvatar || null,
        callType:         current.callType  || "voice",
        duration:         durationStr,
        bytesSent:        latestLog.bytesSent        ?? null,
        bytesReceived:    latestLog.bytesReceived    ?? null,
        networkType:      latestLog.networkType      ?? null,
        callQuality:      latestLog.callQuality      ?? null,
        moments:          callMoments,
        chatId:           current.chatId,
        peerUserId:       current.peerUserId,
      };
      setCallSummary(callSnapshotRef.current);
      setCallSummaryOpen(true);
    }

    resetCallState();
    setCallMoments([]);
  }, [finalizeActiveCallLog, resetCallState, socket, user?.id, callMoments]);


  const createPeerConnection = useCallback((targetUserId, chatId, callType) => {
    const PeerCtor = window.RTCPeerConnection || window.webkitRTCPeerConnection;
    const connection = new PeerCtor(RTC_CONFIG);
    peerRef.current = connection;
    setPeerConn(connection);

    const MediaStreamCtor = window.MediaStream || window.webkitMediaStream;
    const remoteMedia = new MediaStreamCtor();
    remoteStreamRef.current = remoteMedia;
    setRemoteCallStream(remoteMedia);

    connection.ontrack = (event) => {
      event.streams.forEach((stream) => {
        stream.getTracks().forEach((track) => remoteMedia.addTrack(track));
      });
      setCall((prev) => (
        prev.phase === "outgoing" || prev.phase === "connecting"
          ? { ...prev, phase: "active" }
          : prev
      ));
      patchActiveCallLog({ status: "active" });
    };

    connection.onicecandidate = (event) => {
      if (!event.candidate || !socket) return;
      socket.emit("call_ice_candidate", {
        toUserId: targetUserId,
        candidate: event.candidate,
        chatId
      });
    };

    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "connected") {
        setCall((prev) => (
          prev.phase === "active"
            ? prev
            : { ...prev, phase: "active" }
        ));
        patchActiveCallLog({ status: "active" });
      }
      if (["failed", "disconnected", "closed"].includes(connection.connectionState)) {
        endCall(false, "connection_lost");
      }
    };

    audioSenderRef.current = null;
    videoSenderRef.current = null;
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        const sender = connection.addTrack(track, localStreamRef.current);
        if (track.kind === "audio") audioSenderRef.current = sender;
        if (track.kind === "video") videoSenderRef.current = sender;
      });
    }

    setCall((prev) => ({ ...prev, callType, chatId }));
    return connection;
  }, [endCall, socket]);

  const startCall = useCallback(async (callType, options = {}) => {
    if (!socket || !activeChat?.id) {
      notify({ type: "info", message: "Select a chat first." });
      return;
    }
    if (activeChat.chat_type === "group" || activeChat.chat_type === "self") {
      notify({ type: "info", message: "Voice/video call is currently available only for personal chats." });
      return;
    }
    if (!activeChat?.other_user_id) {
      notify({ type: "info", message: "Call target is not available for this chat." });
      return;
    }
    if (activeChat.blocked_by_me) {
      notify({ type: "info", message: "You blocked this user. Unblock to start a call." });
      return;
    }
    if (activeChat.blocked_me) {
      notify({ type: "info", message: "This user blocked you. Call is not available." });
      return;
    }
    if (callRef.current.phase !== "idle") {
      notify({ type: "info", message: "A call is already in progress." });
      return;
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      notify({ type: "error", message: "This browser does not support voice/video calls." });
      return;
    }

    const normalizedType = callType === "video" ? "video" : "voice";
    const callMode = options.mode === "video_chat" ? "video_chat" : "standard";
    const requestAudio = normalizedType === "voice" || callMode !== "video_chat";

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: requestAudio,
        video: normalizedType === "video"
      });
      localStreamRef.current = stream;
      cameraTrackRef.current = stream.getVideoTracks()[0] || null;
      setLocalCallStream(stream);

      const startedAt = new Date().toISOString();
      const logId = appendCallLog(user.id, {
        direction: "outgoing",
        status: "outgoing",
        callType: normalizedType,
        mode: callMode,
        peerUserId: activeChat.other_user_id,
        peerName: activeChat.other_user_name || "Unknown",
        peerAvatar: activeChat.other_user_avatar || null,
        chatId: activeChat.id,
        started_at: startedAt
      });
      callLogIdRef.current = logId;

      setCall({
        phase: "outgoing",
        callType: normalizedType,
        peerUserId: activeChat.other_user_id,
        peerName: activeChat.other_user_name || "Unknown",
        peerAvatar: activeChat.other_user_avatar || null,
        chatId: activeChat.id,
        micEnabled: Boolean(stream.getAudioTracks()[0]?.enabled),
        videoEnabled: Boolean(stream.getVideoTracks()[0]?.enabled),
        screenSharing: false,
        mode: callMode,
        startedAt,
        logId
      });

      const connection = createPeerConnection(activeChat.other_user_id, activeChat.id, normalizedType);
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);

      socket.emit("call_offer", {
        toUserId: activeChat.other_user_id,
        offer,
        chatId: activeChat.id,
        callType: normalizedType,
        mode: callMode
      });
    } catch (err) {
      notify({ type: "error", message: mediaAccessErrorMessage(err, normalizedType) });
      finalizeActiveCallLog("connection_lost", "device_error");
      resetCallState();
    }
  }, [activeChat, createPeerConnection, finalizeActiveCallLog, notify, resetCallState, socket]);

  const acceptIncomingCall = useCallback(async () => {
    const current = callRef.current;
    if (current.phase !== "incoming" || !incomingOfferRef.current || !socket) return;

    if (!navigator?.mediaDevices?.getUserMedia) {
      notify({ type: "error", message: "This browser does not support voice/video calls." });
      socket.emit("call_reject", {
        toUserId: current.peerUserId,
        chatId: current.chatId,
        reason: "device_error"
      });
      finalizeActiveCallLog("device_error", "device_error");
      resetCallState();
      return;
    }

    const requestAudio = current.callType === "voice" || current.mode !== "video_chat";

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: requestAudio,
        video: current.callType === "video"
      });
      localStreamRef.current = stream;
      cameraTrackRef.current = stream.getVideoTracks()[0] || null;
      setLocalCallStream(stream);
      setCall((prev) => ({
        ...prev,
        phase: "connecting",
        micEnabled: Boolean(stream.getAudioTracks()[0]?.enabled),
        videoEnabled: Boolean(stream.getVideoTracks()[0]?.enabled),
        screenSharing: false
      }));
      patchActiveCallLog({ status: "connecting" });

      const connection = createPeerConnection(current.peerUserId, current.chatId, current.callType);
      await connection.setRemoteDescription(new RTCSessionDescription(incomingOfferRef.current));
      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);

      socket.emit("call_answer", {
        toUserId: current.peerUserId,
        answer,
        chatId: current.chatId,
        callType: current.callType,
        mode: current.mode || "standard"
      });
      incomingOfferRef.current = null;
    } catch (err) {
      notify({ type: "error", message: mediaAccessErrorMessage(err, current.callType) });
      socket.emit("call_reject", {
        toUserId: current.peerUserId,
        chatId: current.chatId,
        reason: "device_error"
      });
      finalizeActiveCallLog("device_error", "device_error");
      resetCallState();
    }
  }, [createPeerConnection, finalizeActiveCallLog, notify, patchActiveCallLog, resetCallState, socket]);

  const rejectIncomingCall = useCallback(() => {
    const current = callRef.current;
    if (current.phase !== "incoming" || !socket || !current.peerUserId) return;
    socket.emit("call_reject", {
      toUserId: current.peerUserId,
      chatId: current.chatId,
      reason: "rejected"
    });
    finalizeActiveCallLog("rejected", "rejected");
    resetCallState();
  }, [finalizeActiveCallLog, resetCallState, socket]);

  const stopScreenShare = useCallback(async (silent = false) => {
    if (!callRef.current.screenSharing) return;

    const cameraTrack = cameraTrackRef.current;
    if (videoSenderRef.current) {
      try {
        await videoSenderRef.current.replaceTrack(cameraTrack || null);
      } catch {
        // ignore replace track failures while tearing down
      }
    }

    stopStream(screenStreamRef.current);
    screenStreamRef.current = null;

    if (localStreamRef.current) {
      const previewTracks = [
        ...localStreamRef.current.getAudioTracks(),
        ...(cameraTrack ? [cameraTrack] : [])
      ];
      const MediaStreamCtor = window.MediaStream || window.webkitMediaStream;
      setLocalCallStream(new MediaStreamCtor(previewTracks));
    }

    setCall((prev) => ({
      ...prev,
      screenSharing: false,
      videoEnabled: cameraTrack ? cameraTrack.enabled !== false : prev.videoEnabled
    }));

    if (!silent) {
      notify({ type: "info", message: "Screen sharing stopped." });
    }
  }, [notify]);

  const toggleScreenShare = useCallback(async () => {
    const current = callRef.current;
    if (current.phase === "idle" || current.callType !== "video") return;

    if (current.screenSharing) {
      await stopScreenShare();
      return;
    }

    if (!navigator?.mediaDevices?.getDisplayMedia) {
      notify({ type: "error", message: "Screen share is not supported in this browser." });
      return;
    }

    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false
      });
      const [screenTrack] = displayStream.getVideoTracks();

      if (!screenTrack) {
        stopStream(displayStream);
        notify({ type: "error", message: "No screen track found." });
        return;
      }

      if (videoSenderRef.current) {
        await videoSenderRef.current.replaceTrack(screenTrack);
      }
      screenStreamRef.current = displayStream;

      screenTrack.onended = () => {
        stopScreenShare(true).catch(() => {});
      };

      const previewTracks = [
        ...(localStreamRef.current ? localStreamRef.current.getAudioTracks() : []),
        screenTrack
      ];
      setLocalCallStream(new MediaStream(previewTracks));
      setCall((prev) => ({
        ...prev,
        screenSharing: true,
        videoEnabled: screenTrack.enabled !== false
      }));
      notify({ type: "success", message: "Screen sharing started." });
    } catch {
      notify({ type: "error", message: "Unable to start screen sharing." });
    }
  }, [notify, stopScreenShare]);

  const toggleMic = useCallback(() => {
    const current = callRef.current;
    if (current.phase === "idle") return;

    if (current.mode === "video_chat") {
      notify({ type: "info", message: "Video chat mode keeps microphone disabled." });
      return;
    }

    const audioTrack = localStreamRef.current?.getAudioTracks?.()[0];
    if (!audioTrack) {
      notify({ type: "error", message: "Microphone track is not available." });
      return;
    }

    const next = !audioTrack.enabled;
    audioTrack.enabled = next;
    if (audioSenderRef.current?.track) {
      audioSenderRef.current.track.enabled = next;
    }
    setCall((prev) => ({ ...prev, micEnabled: next }));
  }, [notify]);

  const toggleVideo = useCallback(() => {
    const current = callRef.current;
    if (current.phase === "idle" || current.callType !== "video") return;

    const activeVideoTrack = videoSenderRef.current?.track
      || (current.screenSharing ? screenStreamRef.current?.getVideoTracks?.()[0] : cameraTrackRef.current);

    if (!activeVideoTrack) {
      notify({ type: "error", message: "Video track is not available." });
      return;
    }

    const next = !activeVideoTrack.enabled;
    activeVideoTrack.enabled = next;
    setCall((prev) => ({ ...prev, videoEnabled: next }));
  }, [notify]);

  const addParticipant = useCallback(() => {
    notify({
      type: "info",
      message: "Add participant UI added. Group call signaling can be enabled next."
    });
  }, [notify]);

  useEffect(() => {
    if (!socket) return undefined;

    console.log("[ChatPage] attaching socket listeners", { socketId: socket.id || null });

    const onReceive = (message) => {
      const chatId = Number(message?.chat_id);
      const messageId = message?.id != null ? String(message.id) : "";
      console.log("[ChatPage] receive_message event", { messageId, chatId });
      if (chatId) lastChatMessageEventAtRef.current.set(chatId, Date.now());

      if (messageId) {
        if (deliveredMessageIdsRef.current.has(messageId)) return;
        deliveredMessageIdsRef.current.add(messageId);
        if (deliveredMessageIdsRef.current.size > 1200) {
          const firstKey = deliveredMessageIdsRef.current.values().next().value;
          if (firstKey) deliveredMessageIdsRef.current.delete(firstKey);
        }
      }

      void (async () => {
        const decryptedMessage = await decryptMessageForMe(message);

        // Save incoming message to local IndexedDB
        try {
          await saveLocalMessage(decryptedMessage, user.id);
        } catch (err) {
          console.error("Failed to save incoming message locally:", err);
        }

        let chatExists = false;
        setChats((prev) => {
          const exists = prev.some((chat) => chat.id === decryptedMessage.chat_id);
          chatExists = exists;
          if (!exists) return prev;

          const next = prev.map((chat) => (
            chat.id === decryptedMessage.chat_id
              ? {
                  ...chat,
                  last_message_at: decryptedMessage.created_at,
                  last_message_body: decryptedMessage.body,
                  last_message_image: decryptedMessage.image_url
                }
              : chat
          ));
          const sorted = sortChats(next);
          const target = sorted.find(c => c.id === decryptedMessage.chat_id);
          if (target) saveLocalChat(target, user.id).catch(err => console.error(err));
          return sorted;
        });

        if (!chatExists) {
          try {
            await loadChats();
          } catch (loadErr) {
            console.error("Failed to load new chat in real-time:", loadErr);
          }
        }

        if (decryptedMessage.chat_id === activeChatIdRef.current) {
          setMessages((prev) => {
            if (prev.some((item) => String(item.id) === String(decryptedMessage.id))) return prev;

            const clientId = decryptedMessage.client_message_id;
            const pendingIndex = clientId
              ? prev.findIndex((item) => item.pending && item.client_message_id === clientId)
              : prev.findIndex((item) => (
                item.pending
                && item.sender_id === decryptedMessage.sender_id
                && item.chat_id === decryptedMessage.chat_id
                && (item.body || "") === (decryptedMessage.body || "")
              ));

            if (pendingIndex < 0) return [...prev, decryptedMessage];
            const next = [...prev];
            next[pendingIndex] = decryptedMessage;
            return next;
          });
        } else if (decryptedMessage.sender_id !== user.id) {
          setUnreadByChat((prev) => ({
            ...prev,
            [decryptedMessage.chat_id]: (prev[decryptedMessage.chat_id] || 0) + 1
          }));
        }

        if (decryptedMessage.sender_id !== user.id && decryptedMessage.chat_id !== activeChatIdRef.current) {
          const sourceChat = chatsRef.current.find((item) => Number(item.id) === Number(decryptedMessage.chat_id));
          const senderName = sourceChat?.other_user_name || "New message";
          const preview = decryptedMessage.body
            ? decryptedMessage.body.slice(0, 120)
            : (decryptedMessage.image_url ? "Sent media" : "You have a new message");
          notify({
            type: "info",
            title: senderName,
            message: preview,
            isPriority: true,
            chatId: decryptedMessage.chat_id
          });
        }
      })();
    };

    const onTyping = ({ chatId, userId, name }) => {
      if (userId === user.id || chatId !== activeChatIdRef.current) return;
      const candidate = (name || "").toString().trim();
      if (candidate) {
        setTypingName(candidate);
      } else {
        const sourceChat = chatsRef.current.find((item) => Number(item.id) === Number(chatId));
        const fallback = sourceChat?.chat_type === "group"
          ? "Someone"
          : (sourceChat?.other_user_name || "Someone");
        setTypingName(fallback);
      }
      setTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        setTyping(false);
        setTypingName("");
      }, 1300);
    };

    const onSeen = ({ chatId }) => {
      if (chatId !== activeChatIdRef.current) return;
      setMessages((prev) => prev.map((msg) => (msg.sender_id === user.id ? { ...msg, seen: 1 } : msg)));
    };

    const onChatUpdated = (payload) => {
      const updatedChatId = Number(payload?.chatId);
      if (updatedChatId) {
        const lastMessageAt = lastChatMessageEventAtRef.current.get(updatedChatId);
        if (lastMessageAt && Date.now() - lastMessageAt < 900) return;
      }
      clearTimeout(chatUpdatedTimerRef.current);
      chatUpdatedTimerRef.current = setTimeout(() => {
        void loadChats();
      }, 250);
    };

    const onMessageUpdated = (payload) => {
      if (!payload?.id || payload.chat_id !== activeChatIdRef.current) return;
      void (async () => {
        const decrypted = await decryptMessageForMe(payload);
        setMessages((prev) => prev.map((item) => (
          String(item.id) === String(decrypted.id)
            ? { ...item, ...decrypted }
            : item
        )));
        await saveLocalMessage(decrypted, user.id).catch(() => {});
      })();
    };

    const onMessageDeletedEveryone = (payload) => {
      if (!payload?.messageId || payload.chatId !== activeChatIdRef.current) return;
      setMessages((prev) => prev.map((item) => {
        if (String(item.id) === String(payload.messageId)) {
          const updated = { ...item, body: null, image_url: null, e2ee: null, deleted_for_everyone: true };
          saveLocalMessage(updated, user.id).catch(() => {});
          return updated;
        }
        return item;
      }));
    };

    const onMessageReaction = (payload) => {
      if (!payload?.messageId || payload.chatId !== activeChatIdRef.current) return;
      setMessages((prev) => prev.map((item) => (
        String(item.id) === String(payload.messageId)
          ? { ...item, reactions: payload.reactions || item.reactions || {} }
          : item
      )));
    };

    const onStatus = ({ userId, status, last_seen }) => {
      const statusUpdatedAt = Date.now();
      setChats((prev) => prev.map((chat) => (
        chat.other_user_id === userId
          ? {
              ...chat,
              other_user_status: status,
              other_user_last_seen: last_seen || chat.other_user_last_seen,
              other_user_status_updated_at: statusUpdatedAt
            }
          : chat
      )));
      setPeopleResults((prev) => prev.map((person) => (
        person.id === userId
          ? { ...person, status, last_seen: last_seen || person.last_seen, status_updated_at: statusUpdatedAt }
          : person
      )));
      setActiveChat((prev) => (
        prev && prev.other_user_id === userId
          ? {
              ...prev,
              other_user_status: status,
              other_user_last_seen: last_seen || prev.other_user_last_seen,
              other_user_status_updated_at: statusUpdatedAt
            }
          : prev
      ));
    };

    const onProfileUpdated = (profile) => {
      if (!profile?.id) return;
      if (profile.id === user.id) reload();
      const aboutBio = (profile.about_bio || "").toString();

      setChats((prev) => prev.map((chat) => (
        chat.other_user_id === profile.id
          ? {
              ...chat,
              other_user_name: profile.name,
              other_user_email: profile.email,
              other_user_mobile: profile.mobile,
              other_user_avatar: chat.chat_type === "self" ? (chat.other_user_avatar || "/logo.png") : profile.avatar_url,
              other_user_about: aboutBio
            }
          : chat
      )));
      setPeopleResults((prev) => prev.map((person) => (person.id === profile.id ? { ...person, ...profile } : person)));
      setActiveChat((prev) => (
        prev && prev.other_user_id === profile.id
          ? {
              ...prev,
              other_user_name: profile.name,
              other_user_email: profile.email,
              other_user_mobile: profile.mobile,
              other_user_avatar: prev.chat_type === "self" ? (prev.other_user_avatar || "/logo.png") : profile.avatar_url,
              other_user_about: aboutBio
            }
          : prev
      ));
    };

    const onCallOffer = async (payload) => {
      if (!payload?.fromUserId || !payload.offer) return;
      if (callRef.current.phase !== "idle") {
        socket.emit("call_reject", {
          toUserId: payload.fromUserId,
          chatId: payload.chatId,
          reason: "busy"
        });
        return;
      }

      incomingOfferRef.current = payload.offer;
      const fallback = chatsRef.current.find((chat) => chat.other_user_id === payload.fromUserId);
      const resolvedChatId = payload.chatId || fallback?.id || null;
      if (resolvedChatId && resolvedChatId !== activeChatIdRef.current) {
        const chatById = chatsRef.current.find((chat) => chat.id === resolvedChatId);
        if (chatById) setActiveChat(chatById);
        else if (fallback) setActiveChat(fallback);
        await loadMessages(resolvedChatId);
      }

      notify({
        type: "info",
        title: payload.fromUserName || fallback?.other_user_name || "Incoming call",
        message: payload.callType === "video" ? "Incoming video call" : "Incoming voice call",
        isPriority: true
      });

      const startedAt = new Date().toISOString();
      const logId = appendCallLog(user.id, {
        direction: "incoming",
        status: "incoming",
        callType: payload.callType === "video" ? "video" : "voice",
        mode: payload.mode === "video_chat" ? "video_chat" : "standard",
        peerUserId: payload.fromUserId,
        peerName: payload.fromUserName || fallback?.other_user_name || "Unknown",
        peerAvatar: payload.fromUserAvatar || fallback?.other_user_avatar || null,
        chatId: resolvedChatId,
        started_at: startedAt
      });
      callLogIdRef.current = logId;

      setCall({
        phase: "incoming",
        callType: payload.callType === "video" ? "video" : "voice",
        peerUserId: payload.fromUserId,
        peerName: payload.fromUserName || fallback?.other_user_name || "Unknown",
        peerAvatar: payload.fromUserAvatar || fallback?.other_user_avatar || null,
        chatId: resolvedChatId,
        micEnabled: payload.mode === "video_chat" ? false : true,
        videoEnabled: payload.callType === "video",
        screenSharing: false,
        mode: payload.mode === "video_chat" ? "video_chat" : "standard",
        startedAt,
        logId
      });
    };

    const onCallAnswer = async (payload) => {
      if (!payload?.fromUserId || !payload?.answer) return;
      if (callRef.current.peerUserId !== payload.fromUserId || !peerRef.current) return;
      try {
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(payload.answer));
        setCall((prev) => ({
          ...prev,
          phase: "connecting",
          mode: payload.mode === "video_chat" ? "video_chat" : prev.mode
        }));
        patchActiveCallLog({ status: "connecting" });
      } catch {
        notify({ type: "error", message: "Call connection failed." });
        finalizeActiveCallLog("connection_lost", "connection_failed");
        resetCallState();
      }
    };

    const onCallIce = async (payload) => {
      if (!payload?.fromUserId || !payload?.candidate || !peerRef.current) return;
      if (callRef.current.peerUserId !== payload.fromUserId) return;
      try {
        await peerRef.current.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch {
        // ignore stale ice candidates
      }
    };

    const onCallEnd = (payload) => {
      if (payload?.fromUserId && callRef.current.peerUserId !== payload.fromUserId) return;
      const reason = payload?.reason === "no_answer"
        ? "No answer from user."
        : payload?.reason === "connection_lost"
          ? "Call disconnected."
          : "Call ended.";
      notify({ type: "info", message: reason });
      finalizeActiveCallLog(payload?.reason || "ended", payload?.reason || "ended");
      resetCallState();
    };

    const onCallReject = (payload) => {
      if (payload?.fromUserId && callRef.current.peerUserId !== payload.fromUserId) return;
      const reason = payload?.reason === "busy"
        ? "User is busy on another call."
        : payload?.reason === "device_error"
          ? "Call declined (device permission unavailable)."
          : "Call declined.";
      notify({ type: "info", message: reason });
      finalizeActiveCallLog(payload?.reason || "rejected", payload?.reason || "rejected");
      resetCallState();
    };

    const onCallError = (payload) => {
      notify({ type: "error", message: payload?.message || "Call failed." });
      finalizeActiveCallLog("connection_lost", "error");
      resetCallState();
    };

    const onCallChatMessage = (payload) => {
      if (payload?.chatId === callRef.current.chatId) {
        setCallChatMessages((prev) => [...prev, {
          id: payload.id,
          sender_id: payload.fromUserId,
          body: payload.body,
          created_at: payload.created_at
        }]);
      }
    };

    const onWatchSessionState = (payload) => {
      const chatId = Number(payload?.chatId);
      if (!chatId) return;

      if (payload?.active === false) {
        setWatchSessions((prev) => ({
          ...prev,
          [chatId]: {
            chatId,
            active: false,
            updatedBy: payload?.updatedBy || null,
            updatedAt: payload?.updatedAt || null
          }
        }));
        if (payload?.updatedBy && Number(payload.updatedBy) !== Number(user.id) && chatId === activeChatIdRef.current) {
          notify({ type: "info", message: "Watch Together was stopped." });
        }
        return;
      }

      const nextSession = {
        chatId,
        active: true,
        sourceUrl: (payload?.sourceUrl || "").toString(),
        title: (payload?.title || "").toString(),
        position: Number.isFinite(Number(payload?.position)) ? Number(payload.position) : 0,
        isPlaying: Boolean(payload?.isPlaying),
        playbackRate: Number.isFinite(Number(payload?.playbackRate)) ? Number(payload.playbackRate) : 1,
        updatedBy: payload?.updatedBy || null,
        updatedAt: payload?.updatedAt || null
      };

      setWatchSessions((prev) => ({
        ...prev,
        [chatId]: nextSession
      }));

      if (payload?.updatedBy && Number(payload.updatedBy) !== Number(user.id) && chatId === activeChatIdRef.current) {
        notify({
          type: "info",
          message: nextSession.title
            ? `Watch Together started: ${nextSession.title}`
            : "Watch Together started in this chat."
        });
      }
    };

    const onWatchPlaybackSync = (payload) => {
      const chatId = Number(payload?.chatId);
      if (!chatId) return;

      setWatchSessions((prev) => {
        const existing = prev[chatId];
        if (!existing?.active) return prev;
        return {
          ...prev,
          [chatId]: {
            ...existing,
            position: Number.isFinite(Number(payload?.position)) ? Number(payload.position) : existing.position,
            isPlaying: typeof payload?.isPlaying === "boolean" ? payload.isPlaying : existing.isPlaying,
            playbackRate: Number.isFinite(Number(payload?.playbackRate))
              ? Number(payload.playbackRate)
              : existing.playbackRate,
            updatedBy: payload?.updatedBy || existing.updatedBy,
            updatedAt: payload?.updatedAt || existing.updatedAt
          }
        };
      });
    };

    const onWatchError = (payload) => {
      notify({ type: "error", message: payload?.message || "Watch Together failed." });
    };

    const onMessageDelivered = (payload) => {
      const { messageId, deliveredAt } = payload;
      if (!messageId) return;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            const updated = { ...msg, delivery_status: "delivered", delivered_at: deliveredAt };
            saveLocalMessage(updated, user.id).catch(() => {});
            return updated;
          }
          return msg;
        })
      );
    };

    const onMessageRead = (payload) => {
      const { messageId, readAt } = payload;
      if (!messageId) return;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            const updated = { ...msg, delivery_status: "read", read_at: readAt };
            saveLocalMessage(updated, user.id).catch(() => {});
            return updated;
          }
          return msg;
        })
      );
    };

    const onMessageStatusUpdate = (payload) => {
      const { messageId, status, timestamp } = payload;
      if (!messageId || !status) return;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            const updated = {
              ...msg,
              delivery_status: status,
              ...(status === "delivered" && { delivered_at: timestamp }),
              ...(status === "read" && { read_at: timestamp })
            };
            saveLocalMessage(updated, user.id).catch(() => {});
            return updated;
          }
          return msg;
        })
      );
    };

    const onPollVoteUpdate = (payload) => {
      const { messageId, userId, optionIndex } = payload;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                poll_votes: {
                  ...(msg.poll_votes || {}),
                  [userId]: optionIndex
                }
              }
            : msg
        )
      );
    };

    const onChatPinUpdate = (payload) => {
      const { chatId, pinnedMessageId } = payload;
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId
            ? { ...chat, pinned_message_id: pinnedMessageId }
            : chat
        )
      );
      if (activeChatIdRef.current === chatId) {
        setActiveChat((prev) => prev ? { ...prev, pinned_message_id: pinnedMessageId } : prev);
      }
    };

    const socketEvents = [
      ["receive_message", onReceive],
      ["typing", onTyping],
      ["seen", onSeen],
      ["chat_updated", onChatUpdated],
      ["message_updated", onMessageUpdated],
      ["message_deleted_everyone", onMessageDeletedEveryone],
      ["message_reaction", onMessageReaction],
      ["user_status", onStatus],
      ["user_profile_updated", onProfileUpdated],
      ["call_offer", onCallOffer],
      ["call_answer", onCallAnswer],
      ["call_ice_candidate", onCallIce],
      ["call_end", onCallEnd],
      ["call_reject", onCallReject],
      ["call_error", onCallError],
      ["call_chat_message", onCallChatMessage],
      ["watch_session_state", onWatchSessionState],
      ["watch_playback_sync", onWatchPlaybackSync],
      ["watch_error", onWatchError],
      ["message_delivered", onMessageDelivered],
      ["message_read", onMessageRead],
      ["message_status_update", onMessageStatusUpdate],
      ["poll_vote_update", onPollVoteUpdate],
      ["chat_pin_update", onChatPinUpdate]
    ];

    if (socket && socket.on && socket.off) {
      socketEvents.forEach(([event, handler]) => socket.on(event, handler));
    }

    return () => {
      console.log("[ChatPage] detaching socket listeners", { socketId: socket.id || null });
      if (socket && socket.off) {
        socketEvents.forEach(([event, handler]) => socket.off(event, handler));
      }
      clearTimeout(typingTimer.current);
      clearTimeout(chatUpdatedTimerRef.current);
    };
  }, [loadChats, loadMessages, notify, reload, resetCallState, socket, user?.id]);

  useEffect(() => {
    if (!socket || !activeChat?.id) return;

    console.log("[ChatPage] joining chat room", activeChat.id);
    socket.joinRoom(activeChat.id);

    return () => {
      if (socket && activeChat?.id) {
        console.log("[ChatPage] leaving chat room", activeChat.id);
        socket.leaveRoom(activeChat.id);
      }
    };
  }, [activeChat?.id, socket]);

  useEffect(() => {
    if (!isMobile) setMobileChatOpen(false);
  }, [isMobile]);

  useEffect(() => {
    try {
      const stored = (window.localStorage.getItem(sidebarTabStorageKey) || "").toString();
      if (stored === "status" || stored === "calls" || stored === "chats") setActiveSidebarTab(stored);
      else setActiveSidebarTab("chats");
    } catch {
      setActiveSidebarTab("chats");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarTabStorageKey]);

  useEffect(() => {
    if (!isMobile) return;
    if (activeSidebarTab !== "chats") setMobileChatOpen(false);
  }, [activeSidebarTab, isMobile]);

  useEffect(() => {
    if (call.phase !== "outgoing") return undefined;
    const timer = setTimeout(() => {
      if (callRef.current.phase !== "outgoing") return;
      notify({ type: "info", message: "No answer from user." });
      endCall(false, "no_answer");
    }, 32000);
    return () => clearTimeout(timer);
  }, [call.phase, endCall, notify]);

  useEffect(() => () => {
    toastTimers.current.forEach((timer) => clearTimeout(timer));
    toastTimers.current.clear();
    deliveredMessageIdsRef.current.clear();
    if (callRef.current.phase !== "idle") {
      endCall(true, "ended");
    } else {
      resetCallState();
    }
  }, [endCall, resetCallState]);

  const activeIsGroup = activeChat?.chat_type === "group";
  const activeIsSelf = activeChat?.chat_type === "self";
  const activeBlockedByMe = !activeIsGroup && !activeIsSelf && Boolean(activeChat?.blocked_by_me);
  const activeBlockedMe = !activeIsGroup && !activeIsSelf && Boolean(activeChat?.blocked_me);
  const activeWatchSession = activeChat ? (watchSessions[activeChat.id] || null) : null;
  const partner = activeChat
    ? {
        id: activeIsGroup ? null : activeChat.other_user_id,
        name: activeChat.other_user_name || activeChat.group_name,
        avatar_url: activeChat.other_user_avatar || activeChat.group_avatar_url,
        status: activeIsGroup ? "group" : (activeIsSelf ? "self" : activeChat.other_user_status),
        status_updated_at: activeIsGroup ? null : activeChat.other_user_status_updated_at,
        last_seen: activeIsGroup ? null : activeChat.other_user_last_seen,
        email: activeIsGroup ? null : activeChat.other_user_email,
        mobile: activeIsGroup ? null : activeChat.other_user_mobile,
        about: activeIsGroup ? "" : (activeChat.other_user_about || ""),
        blocked_by_me: activeBlockedByMe,
        blocked_me: activeBlockedMe,
        member_count: Number(activeChat.member_count || 0),
        is_group: activeIsGroup,
        is_self: activeIsSelf
      }
    : null;

  const isChatTab = activeSidebarTab === "chats";
  const showSidebar = !isMobile || (activeSidebarTab === "status" && !selectedStatusFeed) || (!isChatTab || !mobileChatOpen);
  const showRightPane = !isMobile || (isChatTab && mobileChatOpen) || (activeSidebarTab === "status" && selectedStatusFeed);

  const renderedMessages = useMemo(() => {
    return messages.map((m) => {
      if (unlockedMessages.has(Number(m.id))) {
        return unlockedMessages.get(Number(m.id));
      }
      return m;
    });
  }, [messages, unlockedMessages]);

  if (!user?.id) return null;

  return (
    <div className="relative h-[100dvh] overflow-hidden chat-surface p-0">
      <div className="relative grid h-full w-full grid-cols-1 md:grid-cols-[minmax(280px,330px)_minmax(0,1fr)] gap-0">
        {showSidebar && (
          <div className="min-h-0 min-w-0 h-full">
            <SidebarPanel
              me={user}
              theme={theme}
              sidebarColor={sidebarColor}
              isSidebarLight={isSidebarLight}
              activeTab={activeSidebarTab}
              onActiveTabChange={setActiveSidebarTab}
              onOpenProfile={() => setProfileOpen(true)}
              onOpenSettings={() => setSettingsOpen(true)}
              onOpenCallLogs={() => setCallLogsOpen(true)}
              onAdmin={() => {
                navigateTo("admin");
              }}
              onLogout={handleLogout}
              search={search}
              onSearch={setSearch}
              chats={orderedFilteredChats}
              allChats={chats}
              loadingChats={loadingChats}
              activeChatId={activeChat?.id}
              unreadByChat={unreadByChat}
              onSelectChat={selectChat}
              pinnedChatIds={pinnedChatIds}
              onTogglePinChat={togglePinChat}
              onHideChat={hideChatById}
              onDeleteChat={deleteChatById}
              onOpenHiddenVault={() => setHiddenVaultOpen(true)}
              peopleResults={peopleResults}
              searchingPeople={searchingPeople}
              onStartChat={createChat}
              onCreateGroup={openGroupCreator}
              showOnlineStatus={userSettings.showOnlineStatus}
              hiddenChats={hiddenChats}
              hiddenChatsCount={hiddenChatsCount}
              hiddenChatsUnlocked={Boolean(chatPinCache)}
              onUnhideChat={unhideChat}
              compactMode={userSettings.compactMode}
              selectedStatusFeed={selectedStatusFeed}
              onSelectStatusFeed={setSelectedStatusFeed}
              onStartCall={startCall}
              pendingRelationshipRequests={relationshipData?.pendingRequests || []}
            />
          </div>
        )}

        {showRightPane && (
          <div className="min-h-0 min-w-0 h-full panel-in">
            {activeSidebarTab === "status" ? (
              <StatusPanel
                me={user}
                mobile={isMobile}
                selectedStatusFeed={selectedStatusFeed}
                onSelectStatusFeed={setSelectedStatusFeed}
                onBack={() => setSelectedStatusFeed(null)}
              />
            ) : activeSidebarTab === "calls" ? (
              isMobile ? null : (
                <CallsPanel me={user} />
              )
            ) : (
              <ChatPane
                meId={user?.id}
                socket={socket}
                isAdminUser={Boolean(user?.isAdmin)}
                activeChat={activeChat}
                partner={partner}
                pendingRelationshipRequests={relationshipData?.pendingRequests || []}
                onAcceptRelationshipRequest={handleAcceptRelationshipRequest}
                onDeclineRelationshipRequest={handleDeclineRelationshipRequest}
                messages={renderedMessages}
                loadingMessages={loadingMessages}
                typing={typing}
                typingName={typingName}
                uploadBase={API_BASE_URL}
                onTyping={() => socket?.emit("typing", { chatId: activeChat?.id })}
                chatPaneColor={chatPaneColor}
                onSeen={handleSeen}
                onSend={sendMessage}
                replyToMessage={replyToMessage}
                onCancelReply={() => setReplyToMessage(null)}
                enterToSend={userSettings.enterToSend}
                onVoiceCall={() => startCall("voice")}
                onVideoCall={() => startCall("video")}
                onVideoChat={() => startCall("video", { mode: "video_chat" })}
                onOpenCallLogs={() => setCallLogsOpen(true)}
                watchSession={activeWatchSession}
                onSetWatchSource={setWatchSource}
                onClearWatchSession={clearWatchSession}
                onWatchPlaybackSync={syncWatchPlayback}
                onRefreshMessages={refreshActiveChatMessages}
                onSetChatBackground={setChatBackground}
                onClearChatBackground={clearChatBackground}
                onHideChat={hideActiveChat}
                onDeleteChat={deleteChatById}
                onBlockUser={blockActiveUser}
                onUnblockUser={unblockActiveUser}
                onReportUser={reportUser}
                blockActionBusy={blockActionBusy}
                onBackMobile={() => setMobileChatOpen(false)}
                onReply={handleReply}
                onDeleteLocal={removeLocalMessage}
                onEditMessage={editMessage}
                onDeleteForEveryone={deleteForEveryone}
                onToggleStar={toggleStarMessage}
                onReact={reactToMessage}
                onForward={forwardMessage}
                onSelectToggle={toggleSelectMessage}
                onTogglePin={handleTogglePin}
                onVotePoll={handleVotePoll}
                selectedMessageIds={selectedMessageIds}
                onClearSelection={() => setSelectedMessageIds({})}
                compactMode={userSettings.compactMode}
                showOnlineStatus={userSettings.showOnlineStatus}
                isGroupChat={activeIsGroup}
                isSelfChat={activeIsSelf}
                memberCount={Number(activeChat?.member_count || 0)}
                theme={theme}
                notify={notify}
                isChatPaneLight={isChatPaneLight}
                mobile={isMobile}
                onHiddenSearchNavigate={handleHiddenSearchNavigate}
              />
            )}
          </div>
        )}
      </div>

      <ProfileDrawer
        open={profileOpen}
        me={user}
        onClose={() => setProfileOpen(false)}
        onSaved={async (updatedUser, keepOpen) => {
          await reload();
          await loadChats();
          if (!keepOpen) {
            setProfileOpen(false);
          }
        }}
        notify={notify}
      />

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        currentSettings={userSettings}
        onSave={saveUiSettings}
        saving={settingsSaving}
        anaSecurityPinEnabled={Boolean(user?.anaSecurityPinEnabled)}
        onSecurityUpdated={reload}
        theme={theme}
        onToggleTheme={toggleTheme}
        doodleStyle={doodleStyle}
        onSetDoodleStyle={setDoodleStyle}
        accentColor={accentColor}
        onSetAccentColor={setAccentColor}
        onSetSidebarColor={setSidebarColor}
        onSetChatPaneColor={setChatPaneColor}
        sidebarColor={sidebarColor}
        chatPaneColor={chatPaneColor}
        backupStatus={backupStatus}
        backupSize={backupSize}
        lastBackup={lastBackup}
        onCreateBackup={handleCreateBackup}
        onRestoreBackup={handleRestoreBackup}
        onDisableBackup={handleDisableBackup}
        onOpenHiddenVault={() => setHiddenVaultOpen(true)}
        autoLockSetting={autoLockSetting}
        setAutoLockSetting={setAutoLockSetting}
        userId={user?.id}
        onOpenBackupSetup={() => setBackupSetupModalOpen(true)}
      />

      <CreateGroupModal
        open={groupModalOpen}
        users={groupUsers}
        creating={groupCreating}
        onClose={() => setGroupModalOpen(false)}
        onCreate={createGroup}
      />

      <CallOverlay
        call={call}
        localStream={localCallStream}
        remoteStream={remoteCallStream}
        meId={user?.id}
        chatMessages={callChatMessages}
        onSendChat={sendCallChatMessage}
        onAccept={acceptIncomingCall}
        onReject={rejectIncomingCall}
        onEnd={() => endCall(true, "ended")}
        onToggleMic={toggleMic}
        onToggleVideo={toggleVideo}
        onToggleScreenShare={toggleScreenShare}
        onAddParticipant={addParticipant}
        micEnabled={Boolean(call.micEnabled)}
        videoEnabled={Boolean(call.videoEnabled)}
        screenSharing={Boolean(call.screenSharing)}
        socket={socket}
        peerConnection={peerRef.current}
        myName={user?.name || ""}
        myPartnerId={relationshipData?.partnerId || null}
        coupleModeOn={relationshipData?.coupleModeEnabled || false}
        callEffectsEnabled={userSettings.callEffectsEnabled !== false}
        autoReactionsEnabled={userSettings.autoReactionsEnabled !== false}
        onMoment={(moment) => setCallMoments((prev) => [...prev, moment])}
      />

      {/* Post-call summary */}
      {ENABLE_CALL_SUMMARY && (
        <CallSummary
          open={callSummaryOpen}
          callSnapshot={callSummary}
          onClose={() => setCallSummaryOpen(false)}
          onCallAgain={(callType) => {
            setCallSummaryOpen(false);
            if (callSummary?.chatId) {
              const targetChat = chats.find((c) => c.id === callSummary.chatId);
              if (targetChat) {
                setActiveChat(targetChat);
                setTimeout(() => startCall(callType), 100);
              }
            }
          }}
          onMessage={() => {
            setCallSummaryOpen(false);
            if (callSummary?.chatId) {
              const targetChat = chats.find((c) => c.id === callSummary.chatId);
              if (targetChat) setActiveChat(targetChat);
            }
          }}
          onViewDetails={() => setCallSummaryOpen(false)}
          onViewProfile={() => setCallSummaryOpen(false)}
        />
      )}


      <CallLogsDrawer me={user} open={callLogsOpen} onClose={() => setCallLogsOpen(false)} />

      <ToastStack
        toasts={toasts}
        onRemove={dismissToast}
        onReply={sendMessageToChat}
        onPause={pauseToastTimer}
      />

      {modalConfig && (
        <CustomDialogModal
          config={modalConfig}
          onClose={() => setModalConfig(null)}
        />
      )}

      {isOffline && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-950/75 backdrop-blur-md transition-all duration-300">
          <div className="flex w-full max-w-sm flex-col items-center justify-center rounded-3xl border border-white/10 bg-slate-900/90 p-8 text-center shadow-[0_24px_70px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in duration-300">
            <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20 shadow-[0_0_30px_rgba(239,68,68,0.2)]">
              <span className="absolute animate-ping inline-flex h-full w-full rounded-full bg-rose-500/10 opacity-75" />
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-wifi-off">
                <line x1="1" y1="1" x2="23" y2="23"/>
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.5"/>
                <path d="M5 12.5a10.94 10.94 0 0 1 5.83-2.84"/>
                <path d="M8.5 16.5a5 5 0 0 1 7 0"/>
                <path d="M21.3 7A16.9 16.9 0 0 0 18.5 5.5"/>
                <line x1="12" y1="20" x2="12.01" y2="20"/>
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-bold text-slate-100">Connection Lost</h3>
            <p className="mb-6 text-sm text-slate-400">
              You are currently offline. Please check your internet connection. We'll automatically reconnect once you're back online.
            </p>
            <div className="flex items-center gap-2 text-xs font-semibold text-accent">
              <span className="h-2 w-2 rounded-full bg-accent animate-pulse" />
              Waiting for connection...
            </div>
          </div>
        </div>
      )}

      <HideMessageModal
        open={hideModalOpen}
        onClose={() => setHideModalOpen(false)}
        onConfirm={handleHideConfirm}
        busy={hideBusy}
      />

      <UnlockMessageModal
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        onConfirm={handleUnlockConfirm}
        busy={unlockBusy}
        errorMsg={unlockError}
      />

      <ChangeKeyModal
        open={changeKeyModalOpen}
        onClose={() => setChangeKeyModalOpen(false)}
        onConfirm={handleChangeKeyConfirm}
        busy={changeKeyBusy}
        errorMsg={changeKeyError}
      />

      <HiddenVaultDrawer
        open={hiddenVaultOpen}
        onClose={() => setHiddenVaultOpen(false)}
        onUnlockSuccess={(ids) => {
          // Simply triggers updates if needed
        }}
        onOpenChangeKey={(msgId) => {
          const entry = renderedMessages.find(m => Number(m.id) === Number(msgId));
          if (entry) {
            setSelectedMessage(entry);
            setChangeKeyModalOpen(true);
          }
        }}
        onRemoveProtection={(msgId) => {
          if (msgId) {
            setUnlockedMessages((prev) => {
              const next = new Map(prev);
              next.delete(Number(msgId));
              return next;
            });
          }
          if (activeChatIdRef.current) {
            loadMessages(activeChatIdRef.current);
          }
        }}
        unlockedList={Array.from(unlockedMessages.values())}
      />

      <BackupSetupModal
        open={backupSetupModalOpen}
        onClose={() => setBackupSetupModalOpen(false)}
        onConfirm={handleEnableBackupWithPin}
        busy={backupSetupBusy}
      />

      <RestoreBackupModal
        open={restoreBackupModalOpen}
        onClose={() => setRestoreBackupModalOpen(false)}
        onConfirm={handleRestoreBackupWithPin}
        busy={restoreBackupBusy}
        lastBackupInfo={lastBackupInfo}
      />
    </div>
  );
}

function CustomDialogModal({ config, onClose }) {
  const [inputValue, setInputValue] = useState(config.defaultValue || "");
  const [selectedChatIds, setSelectedChatIds] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (config.type === "prompt") {
      inputRef.current?.focus();
    }
  }, [config.type]);

  const handleSubmit = (e) => {
    e.preventDefault();
    config.onConfirm?.(inputValue);
    onClose();
  };

  const toggleSelectChat = (chatId) => {
    setSelectedChatIds((prev) =>
      prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [...prev, chatId]
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-sm rounded-2xl border p-6 shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col gap-4" style={{ backgroundColor: "var(--panel-bg)", borderColor: "var(--border-color, rgba(255,255,255,0.1))", color: "var(--text-primary)" }}>
        <div>
          <h3 className="text-base font-semibold">
            {config.title}
          </h3>
          {config.message && (
            <p className="mt-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
              {config.message}
            </p>
          )}
        </div>

        {config.type === "prompt" && (
          <form onSubmit={handleSubmit} className="w-full">
            <input
              ref={inputRef}
              type={config.isPassword ? "password" : "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={config.placeholder}
              className="w-full rounded-xl border bg-transparent px-3 py-2 text-sm outline-none transition focus:ring-1"
              style={{
                borderColor: "var(--border-color, rgba(255,255,255,0.15))",
                color: "var(--text-primary)",
                "--tw-ring-color": "var(--accent)"
              }}
            />
          </form>
        )}

        {config.type === "forward" && (
          <div className="max-h-60 overflow-y-auto flex flex-col gap-1 pr-1">
            {config.candidates?.map((chat) => {
              const isSelected = selectedChatIds.includes(chat.id);
              return (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => toggleSelectChat(chat.id)}
                  className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/60 transition text-left"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className="h-3.5 w-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500 shrink-0"
                  />
                  <div className="h-8 w-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-xs font-semibold text-violet-700 dark:text-violet-300 shrink-0">
                    {(chat.other_user_name || chat.group_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                      {chat.other_user_name || chat.group_name}
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                      {chat.chat_type === "group" ? "Group Chat" : "Private Chat"}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-2">
          {config.type !== "alert" && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border text-xs font-semibold transition active:scale-[0.98]"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                borderColor: "var(--border-color, rgba(255,255,255,0.1))",
                color: "var(--text-primary)"
              }}
            >
              Cancel
            </button>
          )}
          {config.type !== "forward" ? (
            <button
              type="button"
              onClick={() => {
                config.onConfirm?.(inputValue);
                onClose();
              }}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-white transition active:scale-[0.98]"
              style={{
                backgroundColor: "var(--accent)"
              }}
            >
              OK
            </button>
          ) : (
            <button
              type="button"
              disabled={selectedChatIds.length === 0}
              onClick={() => {
                config.onConfirm?.(selectedChatIds);
                onClose();
              }}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-white transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                backgroundColor: "var(--accent)"
              }}
            >
              Forward ({selectedChatIds.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function playMessageTone(kind = "info") {
  if (typeof window === "undefined") return;
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) return;

  try {
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    const map = {
      success: { type: "triangle", freq: 880 },
      error: { type: "sawtooth", freq: 320 },
      info: { type: "sine", freq: 720 }
    };
    const tone = map[kind] || map.info;
    oscillator.type = tone.type;
    oscillator.frequency.value = tone.freq;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
    oscillator.onended = () => context.close().catch(() => {});
  } catch {
    // ignore audio playback errors
  }
}
